// ============================================================================
// PAGE - canvasBuilder.jsx - COMPLETE FIXED VERSION
// ============================================================================
// client/src/pages/labEngine/canvasBuilder.jsx

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import showNotification from '../../utilities/showNotification';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import SplitCanvasArea from '../../components/labEngine/splitCanvasArea';
import { CourseDomains } from '../../types/courseDomains';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import ComponentCategoryList from '../../components/labEngine/componentCategoryList';
import ComponentConfiguration from '../../modals/labEngine/ComponentConfiguration';
import { validateConnectionRT, validateComponentConfigRT } from '../../utilities/validations/validateCircuit';
import { CATEGORY_CONFIGS, getInitialExpandedState } from '../../utilities/categoryConfig';
import { 
  parseCircuitValidation, 
  filterConnectionValidation, 
  filterProtocolValidation,
  filterI2CAddressValidation,
  filterDependencyValidation,
  filterUnconnectedPins,
  filterConfigurationValidation,
  filterPipelineValidation
} from '../../utilities/validations'

import useComponentLibrary from '../../hooks/useComponentLibrary';
import { GlobalContext } from '../../context/provider';
import useVisualCircuit from '../../hooks/useVisualCircuit';
import useInstructor from '../../hooks/useInstructor';
import useCourse from '../../hooks/useCourse';

import '../../styles/globalstyles.css';
import './canvasBuilder.css';

const CanvasBuilder = () => {
  const navigate = useNavigate();

  const { courseId, labId, domainName } = useParams();
  const [searchParams] = useSearchParams();
  const questionId    = searchParams.get('questionId');
  const questionTitle = searchParams.get('questionTitle') ? decodeURIComponent(searchParams.get('questionTitle')) : null;
  const sessionId = searchParams.get('sessionId');
  const circuitId = searchParams.get('circuitId');
  const gatewayInstanceId = searchParams.get('gatewayInstanceId');
  const isSubCanvas = !!gatewayInstanceId; 

  const domainCanvases = CourseDomains[domainName]?.canvases ?? [];
  const domainConfig = CourseDomains[domainName]?.configModal ?? CourseDomains['IoT'].configModal;
  const domainValidation = CourseDomains[domainName]?.validation ?? CourseDomains['IoT'].validation;
  const domainWireTypes = CourseDomains[domainName]?.wireTypes ?? CourseDomains['IoT'].wireTypes;
  const domainButtonTooltip = CourseDomains[domainName]?.configureButtonTooltip ?? CourseDomains['IoT'].configureButtonTooltip;

  const canvasRef = useRef(null);
  const hardwareCanvasRef = useRef(null);
  const bridgeCanvasRef = useRef(null);
  const cloudCanvasRef = useRef(null);
  const circuitLoadedRef = useRef(false);

  // State management
  const [circuit, setCircuit] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [pinPositions, setPinPositions] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [draggedComponent, setDraggedComponent] = useState(null);
  
  const [connectionMode, setConnectionMode] = useState(false);
  const [tempConnection, setTempConnection] = useState(null);
  const [wirePropertiesModalVisible, setWirePropertiesModalVisible] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState(null);
  
  const [validationResults, setValidationResults] = useState(null);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [showConfigValidationPanel, setShowConfigValidationPanel] = useState(false);
  const [configValidationResults, setConfigValidationResults] = useState(null);
  const [highlightedComponent, setHighlightedComponent] = useState(null);
  const highlightTimeoutRef = useRef(null); // ✅ ADD THIS
  
  const [canvasSettings, setCanvasSettings] = useState({
    zoom: 1,
    panX: 0,
    panY: 0,
    gridSize: 20,
    snapToGrid: true,
    showGrid: true,
    backgroundColor: '#ffffff'
  });

  // Alert states
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  // Course info
  const [courseInfo, setCourseInfo] = useState(null);
  const [labInfo, setLabInfo] = useState(null);
  const [propertiesModalVisible, setPropertiesModalVisible] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasUnsavedProperties, setHasUnsavedProperties] = useState(false);
  const [connectionErrors, setConnectionErrors] = useState([]);
  const [pendingConnection, setPendingConnection] = useState(null);

  // Component library states
  const [addComponentModalVisible, setAddComponentModalVisible] = useState(false);
  const [originalComponent, setOriginalComponent] = useState(null);
  const [newComponentData, setNewComponentData] = useState({
    name: '',
    category: 'sensors',
    needsConfiguration: false,
    icon: '📦',
    pinDetails: '',
    description: '',
    specifications: {},
    simulation: null,
    codeTemplate: null,
    documentation: null,
    createdBy: ''
  });
  
  // Bulk import states
  const [bulkImportModalVisible, setBulkImportModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  
  // View/Edit component states
  const [viewComponentModalVisible, setViewComponentModalVisible] = useState(false);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [selectedInfoComponent, setSelectedInfoComponent] = useState(null);
  const [selectedLibraryComponent, setSelectedLibraryComponent] = useState(null);
  const [isEditingLibraryComponent, setIsEditingLibraryComponent] = useState(false);
  const [editComponentData, setEditComponentData] = useState(null);
  const [showSimulation, setShowSimulation] = useState(false);
  
  // Category expansion state
  const [expandedCategories, setExpandedCategories] = useState(getInitialExpandedState());
  const [searchText, setSearchText] = useState('');
  
  // Full-screen component library modal
  const [fullScreenLibraryVisible, setFullScreenLibraryVisible] = useState(false);
  const [selectedFullScreenComponent, setSelectedFullScreenComponent] = useState(null);
  
  // Communication configuration modal states
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [flowEditorVisible, setFlowEditorVisible]       = useState(false);
  const [selectedGatewayComponent, setSelectedGatewayComponent] = useState(null);
  const [selectedConfigComponent, setSelectedConfigComponent] = useState(null);
  const [componentConfig, setComponentConfig] = useState({
    communicationConfigs: {},
    wirelessConfigs: {},
    pinDetails: []
  });
  const [validationFilter, setValidationFilter] = useState('all');
  const [categorizedValidation, setCategorizedValidation] = useState(null);

  const simulatorLabel = CourseDomains[domainName]?.simulatorLabel || '▶️ 3. Simulate';

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Hooks
  const {
    currentCircuit,
    circuits,
    isLoading,
    error,
    getCircuitById,
    getCircuitByCircuitId,
    getCircuitsByCourse,
    createCircuit,
    updateCircuit,
    validateCircuit,
    syncCircuitToSimulator,
    clearError
  } = useVisualCircuit();

  const { getCourseById } = useCourse();

  const {
    componentsByCategory,
    bulkImport,  
    isLoading: libraryLoading,
    getAllComponents,
    createComponent: createLibraryComponent,
    checkDuplicates,  
    bulkImportComponents,  
    clearImportData,
    updateComponent,
    deleteComponent,
  } = useComponentLibrary();

  const { getInstructorByLoginId } = useInstructor();
 
  const {
    authState: { authData },
  } = useContext(GlobalContext);
      

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const updatePinPositions = useCallback(() => {
    if (!circuit?.components) return;
    
    const positions = {};
    
    circuit.components.forEach(component => {
      const isHardware = domainCanvases.find(c => c.key === 'hardware')?.categories.includes(component.category) ?? false;
      const isBridge = domainCanvases.find(c => c.key === 'bridge')?.categories.includes(component.category) ?? false;
      const isCloud = domainCanvases.find(c => c.key === 'cloud')?.categories.includes(component.category) ?? false;

      const canvasElement = isHardware ? hardwareCanvasRef.current : isBridge ? bridgeCanvasRef.current : cloudCanvasRef.current;
      
      if (!canvasElement) return;
      
      // component.pins?.forEach(pin => {
      component.pinDetails?.forEach(pin => {
        const pinElement = document.querySelector(
          `[data-component-id="${component.instanceId}"][data-pin-name="${pin.pinName}"]`
        );
        
        if (pinElement) {
          const pinRect = pinElement.getBoundingClientRect();
          const canvasRect = canvasElement.getBoundingClientRect();
          
          const centerX = pinRect.left - canvasRect.left + (pinRect.width / 2);
          const centerY = pinRect.top - canvasRect.top + (pinRect.height / 2);
          
          positions[`${component.instanceId}_${pin.pinName}`] = { 
            x: centerX, 
            y: centerY,
            width: pinRect.width,
            canvasType: isHardware ? 'hardware' : 'cloud'
          };
        }
      });
    });
    
    setPinPositions(positions);
  }, [circuit?.components, hardwareCanvasRef, bridgeCanvasRef, cloudCanvasRef]);

  const addConnectionError = (error) => {
    const newError = {
      id: Date.now(),
      timestamp: new Date(),
      ...error
    };
    setConnectionErrors(prev => [...prev, newError]);
  };

  const removeConnectionError = (id) => {
    setConnectionErrors(prev => prev.filter(e => e.id !== id));
  };

  const clearAllErrors = () => {
    setConnectionErrors([]);
  };

// Derive button color based on validation state
const saveButtonColor = hasUnsavedChanges 
  ? "#f59e0b"  // 🟠 orange - unsaved changes
  : validationResults?.errors?.length > 0 
    ? "#ef4444"  // 🔴 red - has errors
    : validationResults?.warnings?.length > 0 
      ? "#f59e0b"  // 🟠 orange - warnings only
      : "#10b981"; // 🟢 green - all clear

const saveButtonTitle = hasUnsavedChanges
  ? "💾 1. Save & Validate *"
  : validationResults?.errors?.length > 0
    ? "❌ 1. Save & Validate"
    : validationResults?.warnings?.length > 0
      ? "⚠️ 1. Save & Validate"
      : "✓ 1. Save & Validate";

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    const loadComponentLibrary = async () => {
      try {
        await getAllComponents();
      } catch (err) {
        console.error('❌ Error loading component library:', err);
      }
    };

    loadComponentLibrary();
  }, []);

  useEffect(() => {
    if (circuit?.components && circuit.components.length > 0) {
      const timer = setTimeout(() => {
        updatePinPositions();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [circuit?.components, updatePinPositions]);

  useEffect(() => {
    const handleUpdate = () => {
      updatePinPositions();
    };
    
    window.addEventListener('resize', handleUpdate);
    canvasRef.current?.addEventListener('scroll', handleUpdate);
    
    return () => {
      window.removeEventListener('resize', handleUpdate);
      canvasRef.current?.removeEventListener('scroll', handleUpdate);
    };
  }, [updatePinPositions]);

  useEffect(() => {
    if (isSubCanvas) return;
    
    const handleFlowSaved = (event) => {
      // console.log('🔔 storage event fired:', event.key, event.newValue);
      if (event.key !== 'NR_FLOW_SAVED') return;
      const { gatewayInstanceId, updatedFlow } = JSON.parse(event.newValue);

      setCircuit(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          components: prev.components.map(c =>
            c.instanceId === gatewayInstanceId
              ? { ...c, nodeRedFlow: updatedFlow }
              : c
          )
        };
      });

      showNotification('Node-RED flow synced — ready to save.', 'success');
      localStorage.removeItem('NR_FLOW_SAVED'); // clean up
    };

    // console.log('👂 storage listener registered, isSubCanvas:', isSubCanvas);

    localStorage.removeItem('NR_FLOW_SAVED');
    window.addEventListener('storage', handleFlowSaved);
    return () => window.removeEventListener('storage', handleFlowSaved);
  }, [isSubCanvas]);

  useEffect(() => {
    // console.log("⏳ Loading circuit ...");
    const loadData = async () => {
      try {
        if (!authData || !authData.id) {
          // console.log('⏳ Waiting for authData...');
          return;
        }

        console.log('✅ AuthData loaded:', authData.id);
        // ✅ WAIT FOR COMPONENT LIBRARY TO LOAD FIRST
        if (Object.keys(componentsByCategory).length === 0) {
          // console.log('⏳ Waiting for component library...');
          return;
        }

        if (courseId) {
          const course = await getCourseById(courseId);
          // console.log('✅ Course loaded:', course);
          setCourseInfo(course.data);
          
          if (labId && course.data?.labs) {
            const lab = course.data.labs.find(l => l.labId === labId);
            if (lab) {
              // console.log('✅ Lab found:', lab);
              setLabInfo(lab);
            } else {
              console.warn('⚠️ Lab not found in course');
            }
          }
        }

        if (isSubCanvas && sessionId) {
            // ── Sub-canvas mode ────────────────────────────────────────────────
            await handleLoadSubCanvas(sessionId, gatewayInstanceId);
        }
        else if (sessionId && !circuitId) {
            await handleLoadOrCreateCircuitForSession();
        }
        else if (circuitId) {
            await handleLoadCircuit(circuitId);
        }
        else {
            initializeEmptyCircuit();
        }

        setIsInitializing(false);
      } catch (err) {
        console.error('Error loading circuit builder data:', err);
        setIsInitializing(false);
      }
    };

    loadData();
  }, [courseId, labId, sessionId, circuitId, authData?.id]);

  useEffect(() => {
      updatePinPositions();
  }, [circuit?.connections, circuit?.components, updatePinPositions]);

  

  // ============================================================================
  // CIRCUIT MANAGEMENT HANDLERS
  // ============================================================================

  const initializeEmptyCircuit = () => {
    console.log('🔧 initializeEmptyCircuit called');

    if (!authData || !authData.id) {
      console.error('❌ Cannot initialize circuit: authData not available');
      return;
    }

    if (!courseId) {
      console.error('❌ Cannot initialize circuit: courseId not available');
      return;
    }

    const emptyCircuit = {
      studentId: authData.id,
      courseId: courseId,
      labId: labId || null,
      questionId: questionId || null,
      sessionId: sessionId || null,
      name: sessionId
        ? `${labId || 'Lab'} - ${questionTitle || questionId || 'IoT Circuit'}`
        : labId
          ? `${labId} - ${questionTitle || questionId || 'Circuit'}`
          : 'New Practice Circuit',
      description: sessionId 
        ? `Circuit for IoT session ${sessionId}` 
        : 'Circuit design in progress',
      boardType: 'ESP32',
      boardPosition: { x: 400, y: 300 },
      components: [],
      connections: [],
      canvasSettings: canvasSettings,
      status: 'draft'
    };

    console.log('✅ Empty circuit initialized:', emptyCircuit);
    setCircuit(emptyCircuit);
  };

  const handleCreateCircuitForIoTSession = async () => {
    console.log('🔧 handleCreateCircuitForIoTSession called');
    
    if (!authData || !authData.id) {
      console.error('❌ Cannot create circuit: authData not available');
      return;
    }

    if (!courseId || !labId || !sessionId) {
      console.error('❌ Cannot create circuit: missing required params');
      return;
    }

    try {
      const circuitData = {
        studentId: authData.id,
        courseId: courseId,
        labId: labId,
        questionId: questionId || null,
        sessionId: sessionId,
        name: `${labInfo?.title || 'Lab'} - IoT Circuit`,
        name: `${labId} - ${questionTitle || questionId } - IoT Circuit`,
        description: `Circuit for IoT session ${sessionId}`,
        boardType: 'ESP32',
        boardPosition: { x: 400, y: 300 },
        components: [],
        connections: [],
        canvasSettings: canvasSettings,
        status: 'draft'
      };

      console.log('📤 Creating circuit in database:', circuitData);
      const response = await createCircuit(circuitData);

      if (response.success) {
        console.log('✅ Circuit auto-created:', response.data);
        setCircuit(response.data);
        setHasUnsavedChanges(false);
        
        const newUrl = `${window.location.pathname}?sessionId=${sessionId}&circuitId=${response.data.circuitId}`;
        window.history.replaceState({}, '', newUrl);
        
        showNotification('Circuit created and linked to IoT session!', 'success');
      } else {
        console.error('❌ Failed to create circuit:', response.error);
        alert('❌ Failed to create circuit for IoT session');
        initializeEmptyCircuit();
      }
    } catch (err) {
      console.error('❌ Error creating circuit for IoT session:', err);
      alert('❌ Error creating circuit');
      initializeEmptyCircuit();
    }
  };

  const handleLoadOrCreateCircuitForSession = async () => {
    console.log('🔧 handleLoadOrCreateCircuitForSession called');
    
    if (!authData || !authData.id) {
      console.error('❌ Cannot load/create circuit: authData not available');
      return;
    }

    if (!courseId || !sessionId) {
      console.error('❌ Cannot load/create circuit: missing required params');
      return;
    }

    try {
      console.log('🔍 Searching for existing circuit with sessionId:', sessionId);
      
      const existingCircuits = await getCircuitsByCourse(courseId, authData.id);
      
      if (existingCircuits.success) {
        const existingCircuit = existingCircuits.data.find(
          c => c.sessionId === sessionId ||
               (c.courseId === courseId && c.labId === labId && c.questionId === questionId)
        );
        
        if (existingCircuit) {
          console.log('✅ Found existing circuit:', existingCircuit.circuitId);
          console.log('🔍 existingCircuit.isValid:', existingCircuit.isValid); // ✅ Add this
          console.log('🔍 Full existingCircuit:', existingCircuit); // ✅ Add this
          
          setCircuit(existingCircuit);
          setCanvasSettings(existingCircuit.canvasSettings || canvasSettings);

          const needsValidation = (
            existingCircuit.isValid === false || 
            existingCircuit.isValid === undefined || 
            existingCircuit.isValid === null
          );
          
          setHasUnsavedChanges(needsValidation);
          setHasUnsavedProperties(false);
          console.log('✅ Circuit loaded. isValid:', existingCircuit.isValid, 'Needs validation:', needsValidation); // ✅ Update this
  
        
          const newUrl = labId 
            ? `/labengine/course/${courseId}/lab/${labId}/domain/${domainName}/buildcanvas?sessionId=${sessionId}`
            : `/labengine/course/${courseId}/domain/${domainName}/buildcanvas?sessionId=${sessionId}`;
          window.history.replaceState({}, '', newUrl);
          
          showNotification('Circuit loaded successfully!', 'success');
          return;
        }
      }
      
      console.log('📝 No existing circuit found, creating new one...');
      await handleCreateCircuitForIoTSession();
      
    } catch (err) {
      console.error('❌ Error loading/creating circuit for session:', err);
      alert('❌ Error loading circuit');
      initializeEmptyCircuit();
    }
  };

  const handleLoadCircuit = async (id) => {
    try {
      console.log('📂 Loading circuit by ID:', id);
      
      const isCircuitId = id.startsWith('CIR');
      
      const response = isCircuitId 
        ? await getCircuitByCircuitId(id)
        : await getCircuitById(id);
      
      if (response.success) {
        console.log('✅ Circuit loaded:', response.data);
        console.log('✅ Circuit isValid from DB:', response.data.isValid); 
      
        
        // ✅ MERGE COMPONENT LIBRARY DATA
        const allLibraryComponents = Object.values(componentsByCategory).flat();
        
        const enrichedComponents = response.data.components?.map(circuitComponent => {
          const libraryComponent = allLibraryComponents.find(
            lib => lib.componentId === circuitComponent.componentId
          );

          if (libraryComponent) {
            return {
              ...circuitComponent,
              // ── Static display fields always come from library ──────────────
              name:             libraryComponent.name,
              needsConfiguration: libraryComponent.needsConfiguration,
              specifications:   libraryComponent.specifications,
              description:      libraryComponent.description,
              icon:             libraryComponent.icon,
              visual:           libraryComponent.visual,
              documentation:    libraryComponent.documentation,
              simulation:       libraryComponent.simulation,
              codeTemplate:     libraryComponent.codeTemplate,
              isFlowEngine:     libraryComponent.isFlowEngine === true,

              // ── Config fields: prefer saved circuit values, fall back to library ─
              communicationConfigs: Object.keys(circuitComponent.communicationConfigs || {}).length > 0
                ? circuitComponent.communicationConfigs       // ← student's saved config
                : libraryComponent.communicationConfigs,      // ← only use library as fallback

              wirelessConfigs: Object.keys(circuitComponent.wirelessConfigs || {}).length > 0
                ? circuitComponent.wirelessConfigs
                : libraryComponent.wirelessConfigs,

              pinDetails: circuitComponent.pinDetails?.length > 0
                ? circuitComponent.pinDetails                 // ← saved pin states
                : libraryComponent.pinDetails,

              // nodeRedFlow always from circuit (student's work, never from library)
              nodeRedFlow: circuitComponent.nodeRedFlow || null,

              // protocolLibraries always from library (code generation helper, not configurable)
              protocolLibraries: libraryComponent.protocolLibraries || {},

              pins: circuitComponent.pins || libraryComponent.pinDetails?.map(pd => ({
                pinName: pd.pinName,
                description: pd.description,
                connectedTo: null,
                visible: true
              })),
            };
          }

          return circuitComponent;
        }) || [];

        const completeCircuit = {
          ...response.data,
          components: enrichedComponents,
          studentId: response.data.studentId || authData.id,
          courseId: response.data.courseId || courseId,
          labId: response.data.labId || labId
        };
        
        setCircuit(completeCircuit);
        setCanvasSettings(completeCircuit.canvasSettings || canvasSettings);
        
        // ✅ NEW: Check if circuit needs validation
        const needsValidation = (
          completeCircuit.isValid === false || 
          completeCircuit.isValid === undefined || 
          completeCircuit.isValid === null
        );
        setHasUnsavedChanges(needsValidation);
        setHasUnsavedProperties(false);
        
        console.log('✅ Circuit loaded. isValid:', completeCircuit.isValid, 'Needs validation:', needsValidation);
        
      } else {
        console.error('❌ Failed to load circuit:', response.error);
        alert('❌ Failed to load circuit');
      }
    } catch (err) {
      console.error('❌ Error loading circuit:', err);
      alert('❌ Error loading circuit');
    }
  };

  const handleSaveAndValidate = async () => {
    try {
      console.log('💾 Save & Validate button clicked');

      // ── Sub-canvas mode: save nodes/wires back into the gateway ──────────
      if (isSubCanvas) {
        await handleSaveSubCanvas();
        return;
      }

      if (!circuit) {
        alert('⚠️ No circuit to save');
        return;
      }

      if (!circuit.studentId || !circuit.courseId) {
        alert('⚠️ Missing required circuit data');
        return;
      }

      console.log('✅ Validation passed');

      // ✅ Prepare circuit data with sessionId if needed
      let circuitToSave = circuit;

      if (!circuit.sessionId) {
        // const newSessionId = `SESSION_${Date.now()}_${authData.id}_practice`;
        const newSessionId = circuit.labId
          ? `SESSION_${Date.now()}_${authData.id}_${circuit.labId}`
          : `SESSION_${Date.now()}_${authData.id}_practice`;
          
        console.log('✅ Generated new sessionId for practice circuit:', newSessionId);
        
        circuitToSave = { ...circuit, sessionId: newSessionId };
        setCircuit(circuitToSave);
      }

      let response;

      // STEP 1: SAVE (UNCONDITIONAL - no validation check)
      if (circuit._id) {
        // console.log('📝 Updating existing circuit with _id:', circuit._id);
        // console.log('💾 components being saved:', JSON.stringify(
        //   circuitToSave.components.map(c => ({
        //     instanceId: c.instanceId,
        //     nodeRedFlowNodeCount: c.nodeRedFlow?.nodes?.length ?? 'NO nodeRedFlow'
        //   }))
        // ));
        response = await updateCircuit(circuit._id, {
          ...circuitToSave,
          canvasSettings: canvasSettings
        });
      } else {
        console.log('✨ Creating new circuit in database');
        response = await createCircuit({
          ...circuitToSave,
          canvasSettings: canvasSettings
        });
      }

      console.log('💾 Save response:', response);

      // Check if save succeeded
      if (!response.success) {
        addConnectionError({
          type: 'error',
          title: '❌ Failed to Save Circuit',
          message: response.error || 'Unknown error occurred while saving',
          details: 'Please check your connection and try again'
        });
        return; // ✅ Stop if save fails
      }

      // ✅ Update circuit with saved data
      const savedCircuitId = response.data._id;
      setCircuit(prev => ({
        ...prev,
        _id: response.data._id,
        circuitId: response.data.circuitId,
        sessionId: response.data.sessionId,
        updatedAt: response.data.updatedAt,
        createdAt: response.data.createdAt,
        __v: response.data.__v,
      }));
      
      setHasUnsavedChanges(false); // ✅ Clear unsaved flag after successful save

      if (!circuitId && response.data.circuitId) {
        const newUrl = `${window.location.pathname}?circuitId=${response.data.circuitId}`;
        window.history.replaceState({}, '', newUrl);
      }

      // STEP 2: VALIDATE (after save)
      console.log('🔍 Starting validation...');
      const validationResponse = await validateCircuit(savedCircuitId);

      if (validationResponse.success) {
         console.log('🔍 Raw validation response:', JSON.stringify(validationResponse.data, null, 2));
        // ✅ Parse and categorize validation results
        const parsed = parseCircuitValidation(validationResponse);
        
        // ✅ Filter by categories
        const categorizedValidation = {
          all: parsed,
          connections: filterConnectionValidation(parsed),
          protocols: filterProtocolValidation(parsed),
          i2cAddresses: filterI2CAddressValidation(parsed),
          dependencies: filterDependencyValidation(parsed),
          configuration: filterConfigurationValidation(parsed),
          unconnectedPins: filterUnconnectedPins(parsed),
          // AI pipeline order filter
          pipeline: filterPipelineValidation(parsed),
        };

        console.log('📊 Categorized validation:', {
          totalErrors: parsed.errors.length,
          totalWarnings: parsed.warnings.length,
          connectionErrors: categorizedValidation.connections.errors.length,
          protocolErrors: categorizedValidation.protocols.errors.length,
          i2cErrors: categorizedValidation.i2cAddresses.errors.length,
          configurationErrors: categorizedValidation.configuration.errors.length,  
          configurationWarnings: categorizedValidation.configuration.warnings.length
        });

        setCategorizedValidation(categorizedValidation);
        // setValidationResults(validationResponse.data);
        setValidationResults(parsed);
        setShowValidationPanel(true);
        
        setCircuit(prev => ({
          ...prev,
          isValid: parsed.valid,
          validationErrors: parsed.errors || []
        }));

        // ✅ CLEANED UP: Show notification based on results
        if (parsed.errors.length === 0 && parsed.warnings.length === 0) {
          showNotification('Circuit saved and validated successfully!', 'success');
        } else if (parsed.errors.length > 0) {
          // Build error summary
          const errorSummary = [];
          if (categorizedValidation.connections.errors.length > 0) {
            errorSummary.push(`${categorizedValidation.connections.errors.length} connection error(s)`);
          }
          if (categorizedValidation.protocols.errors.length > 0) {
            errorSummary.push(`${categorizedValidation.protocols.errors.length} protocol error(s)`);
          }
          if (categorizedValidation.i2cAddresses.errors.length > 0) {
            errorSummary.push(`${categorizedValidation.i2cAddresses.errors.length} I2C conflict(s)`);
          }
          
          const summary = errorSummary.length > 0 ? errorSummary.join(', ') : 'validation errors';
          showNotification(`Circuit not saved, it has ${summary}. Check the validation panel.`, 'warning');
        } else if (parsed.warnings.length > 0) {
          // Only warnings, no errors
          showNotification(`Circuit saved with ${parsed.warnings.length} warning(s). Check the validation panel.`, 'warning');
        }
      } else {
        showNotification('Circuit saved but validation failed', 'warning');
      }

    } catch (err) {
      console.error('❌ Error in save & validate:', err);
      alert('❌ Failed to save/validate circuit: ' + err.message);
    }
  };

  // ✅ New handler function
  const handleCancelPropertyChanges = () => {
    if (hasUnsavedProperties) {
      setAlertConfig({
        title: 'Unsaved Changes',
        icon: '⚠️',
        message: 'You have unsaved property changes. Discard changes and close?',
        onConfirm: () => {
          // Revert changes
          setCircuit(prev => ({
            ...prev,
            components: prev.components.map(c =>
              c.instanceId === selectedComponent.instanceId
                ? originalComponent
                : c
            )
          }));
          setPropertiesModalVisible(false);
          setHasUnsavedProperties(false);
          setAlertVisible(false);
        }
      });
      setAlertVisible(true);
    } else {
      setPropertiesModalVisible(false);
    }
  };

  const handleSyncToSimulator = async () => {
    try {
      if (!circuit._id && !circuit.circuitId) {
        showNotification('Please save the circuit first!', 'error');
        return;
      }

      if (!circuit?.isValid) {
        showNotification('Please validate the circuit first!', 'error');
        return;
      }

      const response = await syncCircuitToSimulator(circuit._id);

      if (response.success) {
        showNotification('Circuit synced to simulator!', 'success');
        setCircuit(prev => ({
          ...prev,
          isSynced: true,
        }));
        await updateCircuit(circuit._id, { 
          ...circuit, 
          isSynced: true,  
        });

        if (response.data?.sessionId) {
          setCircuit(prev => ({
            ...prev,
            sessionId: response.data.sessionId
          }));
          // Auto-save to database
        }
      } else {
        showNotification(`Failed to sync: ${response.error || response.message}`, 'error');
      }
    } catch (err) {
      console.error('Error syncing to simulator:', err);
      showNotification('Failed to sync to simulator', 'error');
    }
  };

  const handleRunSimulator = () => {
    if (!circuit?.sessionId) {
      alert('⚠️ Please sync to simulator first!');
      return;
    }

    const simulatorId = CourseDomains[domainName]?.simulator ?? 'simulator';
    const simulatorRoute = CourseDomains[domainName]?.simulator || 'simulator';
    navigate(`/labengine/course/${courseId}/${simulatorRoute}?sessionId=${circuit.sessionId}`);  
    // navigate(`/labengine/course/${courseId}/${simulatorId}?sessionId=${circuit.sessionId}`);  
  };

  // ============================================================================
  // COMPONENT LIBRARY HANDLERS
  // ============================================================================

  const handleCreateLibraryComponent = async () => {
    try {
      // if (!newComponentData.name || !newComponentData.category || !newComponentData.pins) {
      if (!newComponentData.name || !newComponentData.category || !newComponentData.pinDetails) {
        alert('⚠️ Please fill in all required fields (Name, Category, Pins)');
        return;
      }

      // const pinsArray = newComponentData.pins
      const pinsArray = newComponentData.pinDetails
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      if (pinsArray.length === 0) {
        alert('⚠️ Please add at least one pin');
        return;
      }

      const componentData = {
        name: newComponentData.name,
        category: newComponentData.category,
        needsConfiguration: newComponentData.needsConfiguration,
        icon: newComponentData.icon,
        pinDetails: pinsArray,
        description: newComponentData.description,
        specifications: newComponentData.specifications,
        simulation: newComponentData.simulation,
        codeTemplate: newComponentData.codeTemplate,
        documentation: newComponentData.documentation,
        createdBy: newComponentData.createdBy
      };

      const response = await createLibraryComponent(componentData);

      if (response.success) {
        showNotification('Component added to library!', 'success');
        await getAllComponents();

        setAddComponentModalVisible(false);
        setNewComponentData({
          name: '',
          category: 'sensors',
          needsConfiguration: false,
          icon: '📦',
          pinDetails: '',
          description: '',
          specifications: {},
          simulation: null,
          codeTemplate: null,
          documentation: null,
          createdBy: ''
        });
      } else {
        alert(`❌ Failed to create component: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error creating component:', err);
      alert('❌ Failed to create component');
    }
  };

  const handleOpenFullScreenLibrary = () => {
    setFullScreenLibraryVisible(true);
  };

  const handleCloseFullScreenLibrary = () => {
    setFullScreenLibraryVisible(false);
    setSelectedFullScreenComponent(null);
  };

  const handleSelectComponentCard = (component) => {
    setSelectedFullScreenComponent(component);
  };

  // ============================================================================
  // BULK IMPORT HANDLERS
  // ============================================================================

  const handleOpenBulkImportModal = () => {
    if (authData.role !== 'instructor' && authData.role !== 'admin') {
      alert('⚠️ Only instructors and admins can bulk import components!');
      return;
    }
    setBulkImportModalVisible(true);
    setSelectedCategory('');
    setDuplicateCheckResult(null);
    setShowDuplicateWarning(false);
  };

  const handleCheckDuplicates = async () => {
    if (!selectedCategory) {
      alert('⚠️ Please select a category first!');
      return;
    }

    try {
      const response = await checkDuplicates(selectedCategory);
      
      if (response.success) {
        setDuplicateCheckResult(response.data);
        
        if (response.data.duplicates.length > 0) {
          setShowDuplicateWarning(true);
        } else {
          await handleBulkImport(false);
        }
      } else {
        alert(`❌ Failed to check duplicates: ${response.error}`);
      }
    } catch (err) {
      console.error('Error checking duplicates:', err);
      alert('❌ Error checking duplicates');
    }
  };

  const handleBulkImport = async (shouldUpdate = false) => {
    if (!selectedCategory) {
      alert('⚠️ Please select a category first!');
      return;
    }

    if (!authData?.id) {
      alert('❌ User authentication error. Please login again.');
      return;
    }

    try {
      const response = await bulkImportComponents(selectedCategory, shouldUpdate, authData.id);
      
      if (response.success) {
        const result = response.data;
        
        showNotification(
          `Import Complete!\nImported: ${result.imported}\nUpdated: ${result.updated}\nSkipped: ${result.skipped}\nErrors: ${result.errors.length}`,
          'success'
        );
        
        setBulkImportModalVisible(false);
        setSelectedCategory('');
        setDuplicateCheckResult(null);
        setShowDuplicateWarning(false);
        clearImportData();
        
        await getAllComponents();
      } else {
        alert(`❌ Import failed: ${response.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error bulk importing:', err);
      alert('❌ Error importing components');
    }
  };

  // ============================================================================
  // VIEW/EDIT COMPONENT HANDLERS
  // ============================================================================

  const handleViewLibraryComponent = async (component, e) => {
    e.stopPropagation();
    
    const response = await getInstructorByLoginId(component?.createdBy);
    if (response?.success) {
      component.createdByName = response.data.name;
    } else {
      component.createdByName = 'Prof-iX';
    }

    setSelectedLibraryComponent(component);
    
    setEditComponentData({
      name: component.name,
      category: component.category,
      needsConfiguration: component.needsConfiguration,
      icon: component.icon,
      // pins: Array.isArray(component.pins) ? component.pins.join(', ') : component.pins,
      pinDetails: Array.isArray(component.pinDetails) ? component.pinDetails.join(', ') : component.pinDetails,
      description: component.description || '',
      specifications: component.specifications || {},
      simulation: component.simulation || null,
      codeTemplate: component.codeTemplate || null,
      documentation: component.documentation || null
    });
    setIsEditingLibraryComponent(false);
    setViewComponentModalVisible(true);
  };

  const handleShowComponentInfo = (component, e) => {
    e.stopPropagation();
    setSelectedInfoComponent(component);
    setInfoModalVisible(true);
  };

  const handleUpdateLibraryComponent = async () => {
    if (!selectedLibraryComponent) return;

    try {

      const pinsArray = typeof editComponentData.pinDetails === 'string'
        ? editComponentData.pinDetails.split(',').map(p => p.trim()).filter(p => p.length > 0)
        : editComponentData.pinDetails;

      const updateData = {
        name: editComponentData.name,
        category: editComponentData.category,
        needsConfiguration: editComponentData.needsConfiguration,
        icon: editComponentData.icon,
        pinDetails: pinsArray,
        description: editComponentData.description,
        specifications: editComponentData.specifications,
        simulation: editComponentData.simulation,
        codeTemplate: editComponentData.codeTemplate,
        documentation: editComponentData.documentation
      };

      const response = await updateComponent(selectedLibraryComponent.componentId, updateData);

      if (response.success) {
        showNotification('Component updated successfully!', 'success');
        setViewComponentModalVisible(false);
        setSelectedLibraryComponent(null);
        setIsEditingLibraryComponent(false);
        await getAllComponents();
      } else {
        alert(`❌ Failed to update component: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating component:', err);
      alert('❌ Error updating component');
    }
  };

  const handleDeleteLibraryComponent = async () => {
    if (!selectedLibraryComponent) return;

    setAlertConfig({
      title: 'Delete Component from Library',
      icon: '🗑️',
      message: `Are you sure you want to delete "${selectedLibraryComponent.name}" from the component library? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await deleteComponent(selectedLibraryComponent.componentId);

          if (response.success) {
            showNotification('Component deleted successfully!', 'success');
            setViewComponentModalVisible(false);
            setSelectedLibraryComponent(null);
            setAlertVisible(false);
            await getAllComponents();
          } else {
            alert(`❌ Failed to delete component: ${response.error || 'Unknown error'}`);
          }
        } catch (err) {
          console.error('Error deleting component:', err);
          alert('❌ Error deleting component');
        }
      }
    });
    setAlertVisible(true);
  };

  // ============================================================================
  // CANVAS INTERACTION HANDLERS
  // ============================================================================

  const handleDragStart = (component) => {
    // console.log('🎯 handleDragStart - component:', component);
    // console.log('🎯 handleDragStart - needsConfiguration:', component.needsConfiguration);
    setDraggedComponent(component);
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    
    // console.log('📦 handleDrop - draggedComponent:', draggedComponent);
    // console.log('📦 handleDrop - needsConfiguration:', draggedComponent?.needsConfiguration);
  
    if (!draggedComponent) return;

    // Determine which canvas was dropped on
    let targetCanvas = null;
    let rect = null;

    const canvasRefMap = {
        hardware: hardwareCanvasRef,
        bridge:   bridgeCanvasRef,
        cloud:    cloudCanvasRef,
        training: hardwareCanvasRef,
        test:     cloudCanvasRef,
        flow:     hardwareCanvasRef,
    };

    const domainCanvases = CourseDomains[domainName]?.canvases ?? [];
    for (const canvasConfig of domainCanvases) {
        const ref = canvasRefMap[canvasConfig.key];
        if (ref?.current?.contains(e.target)) {
            targetCanvas = ref.current;
            rect = ref.current.getBoundingClientRect();
            break;
        }
    }

    if (!targetCanvas || !rect) {
      console.log('⚠️ Dropped outside canvas areas');
      return;
    }

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const finalX = canvasSettings.snapToGrid 
      ? Math.round(x / canvasSettings.gridSize) * canvasSettings.gridSize
      : x;
    const finalY = canvasSettings.snapToGrid
      ? Math.round(y / canvasSettings.gridSize) * canvasSettings.gridSize
      : y;

    const newComponent = {
      componentId: draggedComponent.componentId,
      category: draggedComponent.category,
      needsConfiguration: draggedComponent.needsConfiguration,
      componentName: draggedComponent.name,
      name: draggedComponent.name,
      instanceId: `${draggedComponent.componentId}_${Date.now()}`,
      position: { x: finalX, y: finalY },
      pins: draggedComponent.pinDetails.map(pindetail => ({
        pinName: pindetail.pinName,
        description: pindetail.description,
        connectedTo: null,
        visible: true
      })),
      label: `${draggedComponent.name} ${(circuit?.components?.length || 0) + 1}`,
      icon: draggedComponent.icon,
      specifications: draggedComponent.specifications,
      description: draggedComponent.description,
      visual: draggedComponent.visual,
      pinDetails: draggedComponent.pinDetails,
      documentation: draggedComponent.documentation,
      simulation: draggedComponent.simulation,
      codeTemplate: draggedComponent.codeTemplate,
      communicationCapabilities: draggedComponent.communicationCapabilities || [],
      communicationConfigs: draggedComponent.communicationConfigs || {},
      wirelessConfigs: draggedComponent.wirelessConfigs || {},  // ✅ ADD THIS
      protocolLibraries: draggedComponent.protocolLibraries || {},
      isFlowEngine:  draggedComponent.isFlowEngine  || false,
      nodeRedFlow:   draggedComponent.nodeRedFlow    || null,
      nodeRedType:   draggedComponent.nodeRedType    || null,
      rotation: 0,
      scale: 1
    };

    // console.log('✅ newComponent created:', newComponent);
    // console.log('✅ newComponent.needsConfiguration:', newComponent.needsConfiguration);

    setCircuit(prev => ({
      ...prev,
      components: [...(prev?.components || []), newComponent]
    }));

    setHasUnsavedChanges(true);
    setIsDragging(false);
    setDraggedComponent(null);
  };

  const handleComponentDragStart = (e, component) => {
    e.stopPropagation();
    setSelectedComponent(component);
  };

  const handleComponentDragEnd = (e, component) => {
      e.preventDefault();
      
      const domainCanvases = CourseDomains[domainName]?.canvases ?? [];
      const canvasConfig = domainCanvases.find(c =>
          c.categories.map(cat => cat.key).includes(component.category)
      );
      const canvasKey = canvasConfig?.key ?? 'hardware';
      
      const canvasRefMap = {
          hardware: hardwareCanvasRef,
          bridge:   bridgeCanvasRef,
          cloud:    cloudCanvasRef,
          training: hardwareCanvasRef,
          test:     cloudCanvasRef,
          flow:     hardwareCanvasRef,
      };
      
      const targetCanvas = canvasRefMap[canvasKey]?.current;
      
      if (!targetCanvas) return;

      const x = e.canvasX !== undefined ? e.canvasX : e.clientX - targetCanvas.getBoundingClientRect().left;
      const y = e.canvasY !== undefined ? e.canvasY : e.clientY - targetCanvas.getBoundingClientRect().top;

      const finalX = canvasSettings.snapToGrid 
          ? Math.round(x / canvasSettings.gridSize) * canvasSettings.gridSize
          : x;
      const finalY = canvasSettings.snapToGrid
          ? Math.round(y / canvasSettings.gridSize) * canvasSettings.gridSize
          : y;

      setCircuit(prev => ({
          ...prev,
          components: prev.components.map(c =>
              c.componentId === component.componentId
                  ? { ...c, position: { x: finalX, y: finalY } }
                  : c
          )
      }));
      setHasUnsavedChanges(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSelectComponent = (component) => {
    setSelectedComponent(component);
    setOriginalComponent(JSON.parse(JSON.stringify(component)));
    setSelectedConnection(null);
    setHasUnsavedProperties(false);
  };

  const handleDeleteComponent = () => {
    if (!selectedComponent) return;

    setAlertConfig({
      title: 'Delete Component',
      icon: '🗑️',
      message: `Delete ${selectedComponent.label}?`,
      onConfirm: () => {
        setCircuit(prev => ({
          ...prev,
          components: prev.components.filter(c => c.componentId !== selectedComponent.componentId),
          connections: prev.connections.filter(
            conn => conn.from.componentInstanceId !== selectedComponent.instanceId && 
                    conn.to.componentInstanceId !== selectedComponent.instanceId
          )
        }));
        setHasUnsavedChanges(true);
        setSelectedComponent(null);
        setAlertVisible(false);
      }
    });
    setAlertVisible(true);
  };

  const handleUpdateComponentProperty = (property, value) => {
    if (!selectedComponent) return;

    setCircuit(prev => ({
      ...prev,
      components: prev.components.map(c =>
        c.componentId === selectedComponent.componentId
          ? { ...c, [property]: value }
          : c
      )
    }));

    setSelectedComponent(prev => ({ ...prev, [property]: value }));
    setHasUnsavedChanges(true);
    setHasUnsavedProperties(true);
  };

  const handleSaveComponentProperties = async () => {
    if (!hasUnsavedProperties) return;

    // No _id yet — circuit hasn't been created in DB, fall back to full save
    if (!circuit?._id) {
      await handleSaveAndValidate();
      setHasUnsavedProperties(false);
      return;
    }

    try {
      const updatedCircuit = {
        ...circuit,
        isValid: false,
        isSynced: false
      };
      
      const response = await updateCircuit(circuit._id, updatedCircuit);
      if (response.success) {
        showNotification('Properties saved to database. Please validate the circuit.', 'info');
        setCircuit(updatedCircuit);
        setHasUnsavedProperties(false);
      } else {
        showNotification('Failed to save properties!', 'error');
      }
    } catch (err) {
      console.error('Error saving properties:', err);
      showNotification('Failed to save properties!', 'error');
    }
  };

  const handleConfigureComponent = (comp, e) => {
      e.stopPropagation();
      
      const domainCanvases = CourseDomains[domainName]?.canvases ?? [];
      const configCategories = domainCanvases.flatMap(c => 
          c.categories.map(cat => cat.key)
      );
      
      if (!configCategories.includes(comp.category)) {
          showNotification('This component does not require configuration', 'info');
          return;
      }
      
      setSelectedConfigComponent(comp);
      
      setComponentConfig({
          communicationConfigs: comp.communicationConfigs || {},
          wirelessConfigs: comp.wirelessConfigs || {},
          pinDetails: comp.pinDetails || []
      });
      
      setConfigModalVisible(true);
  };

  const handleSaveConfig = async (newConfig, flowConfig) => {
    try {
      const updatedCircuit = {
        ...circuit,
        isValid: false,
        isSynced: false,
        components: circuit.components.map(c =>
          c.instanceId === selectedConfigComponent.instanceId
            ? {
                ...c,
                communicationConfigs: newConfig.communicationConfigs,
                wirelessConfigs:      newConfig.wirelessConfigs,
                pinDetails:           newConfig.pinDetails,
                // ── NEW: merge flowConfig into nodeRedFlow if this is a gateway ──
                ...(c.isFlowEngine && flowConfig ? {
                  nodeRedFlow: {
                    ...(c.nodeRedFlow || {}),
                    flowLabel:       flowConfig.flowLabel,
                    flowDescription: flowConfig.flowDescription,
                    deployMode:      flowConfig.deployMode,
                    autoStart:       flowConfig.autoStart,
                    debug:           flowConfig.debug,
                    nodes:       c.nodeRedFlow?.nodes       || [],
                    wires:       c.nodeRedFlow?.wires       || [],
                    generatedAt: c.nodeRedFlow?.generatedAt || null,
                  }
                } : {})
              }
            : c
        )
      };
      
      // ✅ Update React state
      setCircuit(updatedCircuit);
      setComponentConfig(newConfig);
      
      // ✅ Save to database with isValid: false
      if (updatedCircuit._id) {
        const response = await updateCircuit(updatedCircuit._id, updatedCircuit);
        if (!response.success) {
          showNotification('Failed to save configuration to database!', 'error');
          return;
        }
      }
      
      // ✅ Mark as requiring validation
      setHasUnsavedChanges(true);
      
      showNotification('Configuration saved to database. Please Save & Validate the circuit.', 'info');
    } catch (err) {
      console.error('Error saving configuration:', err);
      showNotification('Failed to save configuration!', 'error');
    }
  }

  // ============================================================================
  // CONNECTION HANDLERS
  // ============================================================================

  const handleStartConnection = (component, pinName) => {
    if (!connectionMode) {
      alert('⚠️ Enable Connection Mode first! Click "🔗 Connect Pins" button.');
      return;
    }
    
    setConnectionStart({ component, pinName });
    setTempConnection({
      from: { component, pinName },
      mousePosition: null
    });
  };

  const handleCompleteConnection = (toComponent, toPinName) => {
    if (!connectionStart) {
      alert('⚠️ Please select a starting pin first!');
      return;
    }

    // ── Sub-canvas: bypass all IoT validation for Node-RED wires ──────────
    if (isSubCanvas) {
      if (!connectionStart) return;

      if (connectionStart.component.instanceId === toComponent.instanceId) {
        showNotification('Cannot connect a node to itself.', 'error');
        setConnectionStart(null);
        setTempConnection(null);
        return;
      }

      const newConnection = {
        connectionId: `conn_${Date.now()}`,
        wireType:  'nr_message',
        wireColor: '#94a3b8',
        from: {
          componentInstanceId: connectionStart.component.instanceId,
          pinName: connectionStart.pinName
        },
        to: {
          componentInstanceId: toComponent.instanceId,
          pinName: toPinName
        },
        isValid: true
      };

      setCircuit(prev => ({
        ...prev,
        connections: [...(prev.connections || []), newConnection]
      }));
      setHasUnsavedChanges(true);
      setConnectionStart(null);
      setTempConnection(null);
      return;  // stop here — no IoT validation
    }

    if (connectionStart.component.instanceId === toComponent.instanceId) {
      alert('⚠️ Cannot connect pins on the same component!');
      setConnectionStart(null);
      setTempConnection(null);
      return;
    }

    // Get full component objects from circuit
    const fromComponentFull = circuit.components.find(
      c => c.instanceId === connectionStart.component.instanceId
    );
    const toComponentFull = circuit.components.find(
      c => c.instanceId === toComponent.instanceId
    );

    // ============================================================
    // DEBUG BLOCK - remove after fix is confirmed
    // ============================================================
    
    const fromPinDetail = fromComponentFull?.pinDetails?.find(p => p.pinName === connectionStart.pinName);
    const toPinDetail = toComponentFull?.pinDetails?.find(p => p.pinName === toPinName);
    // ============================================================

    const realTimeValidation = validateConnectionRT(
      fromComponentFull, 
      connectionStart.pinName, 
      toComponentFull, 
      toPinName,
      circuit
    );

    
    // if (!realTimeValidation.valid) {
    //   addConnectionError({
    //     type: realTimeValidation.severity || 'error',
    //     title: realTimeValidation.severity === 'warning' ? '⚠️ Connection Warning' : '❌ Invalid Connection!',
    //     message: realTimeValidation.message,
    //     details: realTimeValidation.warning,
    //     from: `${connectionStart.component.label} (${connectionStart.pinName})`,
    //     to: `${toComponent.label} (${toPinName})`
    //   });
    //   setConnectionStart(null);
    //   setTempConnection(null);
    //   return;
    // }
    if (!realTimeValidation.valid) {
      const messages = realTimeValidation.message?.split(' | ') || [realTimeValidation.message];
      
      messages.forEach(message => {
        addConnectionError({
          type: realTimeValidation.severity || 'error',
          title: realTimeValidation.severity === 'warning' ? '⚠️ Connection Warning' : '❌ Invalid Connection!',
          message: message,
          details: realTimeValidation.warning,
          from: `${connectionStart.component.label} (${connectionStart.pinName})`,
          to: `${toComponent.label} (${toPinName})`
        });
      });

      setConnectionStart(null);
      setTempConnection(null);
      return;
    }

    if (realTimeValidation.severity === 'warning') {
      setPendingConnection({
        fromComponent: connectionStart.component,
        fromPinName: connectionStart.pinName,
        toComponent,
        toPinName,
        validation: realTimeValidation
      });
      addConnectionError({
        type: 'warning',
        title: '⚠️ Connection Warning',
        message: realTimeValidation.message,
        details: realTimeValidation.warning,
        from: `${connectionStart.component.label} (${connectionStart.pinName})`,
        to: `${toComponent.label} (${toPinName})`
      });
      setConnectionStart(null);
      setTempConnection(null);
      return;
    }

    // Handle warnings (show but allow connection)
    if (realTimeValidation.severity === 'warning') {
      setPendingConnection({
        fromComponent: connectionStart.component,
        fromPinName: connectionStart.pinName,
        toComponent,
        toPinName,
        validation: realTimeValidation
      });
      addConnectionError({
        type: 'warning',
        title: '⚠️ Connection Warning',
        message: realTimeValidation.message,
        details: realTimeValidation.warning,
        from: `${connectionStart.component.label} (${connectionStart.pinName})`,
        to: `${toComponent.label} (${toPinName})`
      });
      setConnectionStart(null);
      setTempConnection(null);
      return; // Stop here — wait for user decision
    }
    
    const existingConnection = circuit?.connections?.find(
      conn =>
        (conn.from.componentInstanceId === connectionStart.component.instanceId &&
        conn.from.pinName === connectionStart.pinName &&
        conn.to.componentInstanceId === toComponent.instanceId &&
        conn.to.pinName === toPinName) ||
        (conn.from.componentInstanceId === toComponent.instanceId &&
        conn.from.pinName === toPinName &&
        conn.to.componentInstanceId === connectionStart.component.instanceId &&
        conn.to.pinName === connectionStart.pinName)
    );

    if (existingConnection) {
      alert('⚠️ Connection already exists between these pins!');
      setConnectionStart(null);
      setTempConnection(null);
      return;
    }

    const newConnection = {
      connectionId: `conn_${Date.now()}`,
      wireColor: realTimeValidation.color,
      wireType: realTimeValidation.connectionType,
      strokeWidth: realTimeValidation.strokeWidth,
      strokeStyle: realTimeValidation.strokeStyle,
      from: {
        componentInstanceId: connectionStart.component.instanceId,
        pinName: connectionStart.pinName
      },
      to: {
        componentInstanceId: toComponent.instanceId,
        pinName: toPinName
      },
      isValid: true
    };

    setCircuit(prev => ({
      ...prev,
      connections: [...(prev?.connections || []), newConnection]
    }));

    setCircuit(prev => ({
      ...prev,
      components: prev.components.map(c => {
        if (c.instanceId === connectionStart.component.instanceId) {
          return {
            ...c,
            // pins: c.pins.map(p =>
            pinDetails: c.pinDetails.map(p =>
              p.pinName === connectionStart.pinName
                ? { ...p, connectedTo: `${toComponent.instanceId}:${toPinName}` }
                : p
            )
          };
        } else if (c.instanceId === toComponent.instanceId) {
          return {
            ...c,
            // pins: c.pins.map(p =>
            pinDetails: c.pinDetails.map(p =>
              p.pinName === toPinName
                ? { ...p, connectedTo: `${connectionStart.component.instanceId}:${connectionStart.pinName}` }
                : p
            )
          };
        }
        return c;
      })
    }));

    setHasUnsavedChanges(true);
    // console.log(`✅ Connection created: ${connectionStart.component.label}:${connectionStart.pinName} → ${toComponent.label}:${toPinName}`);
    
    setTimeout(() => {
      updatePinPositions();
    }, 100);
    
    setConnectionStart(null);
    setTempConnection(null);
  };

  const handleCanvasMouseMove = (e) => {
    if (!connectionStart) return;

    let targetCanvas = null;
    let canvasType = null;

    if (hardwareCanvasRef.current?.contains(e.target) || 
        hardwareCanvasRef.current === e.target) {
      targetCanvas = hardwareCanvasRef.current;
      canvasType = 'hardware';
    } else if (bridgeCanvasRef.current?.contains(e.target) || 
              bridgeCanvasRef.current === e.target) {
      targetCanvas = bridgeCanvasRef.current;
      canvasType = 'cloud';
    } else if (cloudCanvasRef.current?.contains(e.target) || 
              cloudCanvasRef.current === e.target) {
      targetCanvas = cloudCanvasRef.current;
      canvasType = 'cloud';
    }

    if (targetCanvas) {
      const rect = targetCanvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setTempConnection(prev => ({
        ...prev,
        mousePosition: { x, y, canvasType }
      }));
    }
  };

  const handleCancelConnection = () => {
    setConnectionStart(null);
    setTempConnection(null);
    console.log('❌ Connection cancelled');
  };

  const handleDeleteConnection = () => {
    if (!selectedConnection) return;

    setCircuit(prev => ({
      ...prev,
      connections: prev.connections.filter(c => c.connectionId !== selectedConnection.connectionId),
      components: prev.components.map(comp => {
        if (comp.instanceId === selectedConnection.from.componentInstanceId || 
            comp.instanceId === selectedConnection.to.componentInstanceId) {
          return {
            ...comp,
            // pins: comp.pins.map(pin => {
            pinDetails: comp.pinDetails.map(pin => {
              if ((comp.instanceId === selectedConnection.from.componentInstanceId && pin.pinName === selectedConnection.from.pinName) ||
                  (comp.instanceId === selectedConnection.to.componentInstanceId && pin.pinName === selectedConnection.to.pinName)) {
                return { ...pin, connectedTo: null };
              }
              return pin;
            })
          };
        }
        return comp;
      })
    }));

    setHasUnsavedChanges(true);
    setSelectedConnection(null);
    console.log('✅ Connection deleted');
  };

  const handleAcceptPendingConnection = () => {
    if (!pendingConnection) return;

    const { fromComponent, fromPinName, toComponent, toPinName } = pendingConnection;

    const newConnection = {
      connectionId: `conn_${Date.now()}`,
      wireColor: pendingConnection.validation.color,
      wireType: pendingConnection.validation.connectionType,
      strokeWidth: pendingConnection.validation.strokeWidth,
      strokeStyle: pendingConnection.validation.strokeStyle,
      from: {
        componentInstanceId: fromComponent.instanceId,
        pinName: fromPinName
      },
      to: {
        componentInstanceId: toComponent.instanceId,
        pinName: toPinName
      },
      isValid: true
    };

    setCircuit(prev => ({
      ...prev,
      connections: [...(prev?.connections || []), newConnection],
      components: prev.components.map(c => {
        if (c.instanceId === fromComponent.instanceId) {
          return {
            ...c,
            pinDetails: c.pinDetails.map(p =>
              p.pinName === fromPinName
                ? { ...p, connectedTo: `${toComponent.instanceId}:${toPinName}` }
                : p
            )
          };
        } else if (c.instanceId === toComponent.instanceId) {
          return {
            ...c,
            pinDetails: c.pinDetails.map(p =>
              p.pinName === toPinName
                ? { ...p, connectedTo: `${fromComponent.instanceId}:${fromPinName}` }
                : p
            )
          };
        }
        return c;
      })
    }));

    setHasUnsavedChanges(true);
    setPendingConnection(null);
    clearAllErrors();

    setTimeout(() => updatePinPositions(), 100);
  };

  const handleCancelPendingConnection = () => {
    setPendingConnection(null);
    clearAllErrors();
  };

  const getPinPosition = (component, pinName) => {  
    const pinElement = document.querySelector(
      `[data-component-id="${component.instanceId}"][data-pin-name="${pinName}"]`
    );
    
    const componentElement = pinElement?.closest('.placed-component');
    
    if (!pinElement || !componentElement) return null;
    
    const pinRect = pinElement.getBoundingClientRect();
    const componentRect = componentElement.getBoundingClientRect();
    
    // Get the canvas-content element to read the current zoom level
    const canvasContent = componentElement.closest('.canvas-content');
    
    // Extract zoom from transform style
    let zoom = 1;
    if (canvasContent) {
      const transform = canvasContent.style.transform;
      const scaleMatch = transform.match(/scale\(([\d.]+)\)/);
      if (scaleMatch) {
        zoom = parseFloat(scaleMatch[1]);
      }
    }
    
    // Get pin position RELATIVE to component (in screen pixels, affected by zoom)
    const relativeXScreen = pinRect.left - componentRect.left + (pinRect.width / 2);
    const relativeYScreen = pinRect.top - componentRect.top + (pinRect.height / 2);
    
    // Convert to logical space by dividing by zoom
    const relativeXLogical = relativeXScreen / zoom;
    const relativeYLogical = relativeYScreen / zoom;
    
    // Add to component's LOGICAL position to get absolute logical coordinates
    return {
      x: component.position.x + relativeXLogical,
      y: component.position.y + relativeYLogical,
      width: pinRect.width / zoom,
      pinBoxLeft: component.position.x + (pinRect.left - componentRect.left) / zoom,
      pinBoxRight: component.position.x + (pinRect.right - componentRect.left) / zoom
    };
  };

  const getCrossCanvasPosition = useCallback((component, pinName) => {
      const pinPos = getPinPosition(component, pinName);
      if (!pinPos) return null;
      
      const domainCanvases = CourseDomains[domainName]?.canvases ?? [];
      const canvasConfig = domainCanvases.find(c =>
          c.categories.map(cat => cat.key).includes(component.category)
      );
      const canvasKey = canvasConfig?.key ?? 'hardware';

      const canvasRefMap = {
          hardware: hardwareCanvasRef,
          bridge:   bridgeCanvasRef,
          cloud:    cloudCanvasRef,
          training: hardwareCanvasRef,
          test:     cloudCanvasRef,
          flow:     hardwareCanvasRef,
      };

      const canvasRef = canvasRefMap[canvasKey]?.current;
      if (!canvasRef) return pinPos;
      
      const canvasRect = canvasRef.getBoundingClientRect();
      
      return {
          x: pinPos.x + canvasRect.left,
          y: pinPos.y + canvasRect.top,
          width: pinPos.width,
          pinBoxLeft: pinPos.pinBoxLeft + canvasRect.left,
          pinBoxRight: pinPos.pinBoxRight + canvasRect.left
      };
  }, [getPinPosition, hardwareCanvasRef, bridgeCanvasRef, cloudCanvasRef, domainName]);

  const handleUpdateWireProperty = (property, value) => {
    if (!selectedConnection) return;

    setCircuit(prev => ({
      ...prev,
      connections: prev.connections.map(c =>
        c.connectionId === selectedConnection.connectionId
          ? { ...c, [property]: value }
          : c
      )
    }));
    setSelectedConnection(prev => ({ ...prev, [property]: value }));
    setHasUnsavedChanges(true); 
  };
  
  const handleUpdateCircuit = (key, value) => {
    if (!circuit) return;
    
    setCircuit(prev => ({
      ...prev,
      [key]: value
    }));
    
    setHasUnsavedChanges(true);
  };

  const handleOpenConfigModal = () => {
    // ✅ Always get fresh component data from circuit
    const latestComponent = circuit.components.find(
      c => c.instanceId === selectedComponent.instanceId
    );
    
    if (!latestComponent) {
      showNotification('Component not found in circuit', 'error');
      return;
    }
    
    setSelectedConfigComponent(latestComponent);
    setComponentConfig({
      communicationConfigs: latestComponent.communicationConfigs || {},
      wirelessConfigs: latestComponent.wirelessConfigs || {},
      pinDetails: latestComponent.pinDetails || []
    });
    setConfigModalVisible(true);
  };

  const handleOpenFlowEditor = async (gatewayComp) => {

    // First time: generate and persist flowSessionId
    if (!gatewayComp.flowSessionId) {
      const newFlowSessionId = `SESSION_NR_${Date.now()}_${gatewayComp.instanceId}`;

      const updatedComponents = circuit.components.map(c =>
        c.instanceId === gatewayComp.instanceId
          ? { ...c, flowSessionId: newFlowSessionId }
          : c
      );

      const updatedCircuit = { ...circuit, components: updatedComponents };
      setCircuit(updatedCircuit);

      if (circuit._id) {
        const response = await updateCircuit(circuit._id, updatedCircuit);
        if (!response.success) {
          showNotification('Failed to initialise flow session. Please try again.', 'error');
          return;
        }
      }

      // Use the updated component with the new flowSessionId for the modal
      gatewayComp = { ...gatewayComp, flowSessionId: newFlowSessionId };
    }

    // Open the summary modal — modal button calls window.open
    setSelectedGatewayComponent(gatewayComp);
    setFlowEditorVisible(true);
  };

  // ============================================================================
  // FIX 2: handleLoadSubCanvas
  // — Enrich each node from component library (restores needsConfiguration,
  //   icon, visual, pinDetails schema, etc.)
  // — Restore connectedTo on pinDetails from the wire list
  // ============================================================================
  const handleLoadSubCanvas = async (flowSessionId, gwInstanceId) => {
    try {
      console.log('🔴 Sub-canvas mode — loading flow for gateway:', gwInstanceId);

      const allCircuits = await getCircuitsByCourse(courseId, authData.id);
      if (!allCircuits.success) {
        showNotification('Could not load parent circuit.', 'error');
        return;
      }

      const parentCircuit = allCircuits.data.find(c =>
        c.components?.some(
          comp => comp.instanceId === gwInstanceId &&
                  comp.flowSessionId === flowSessionId
        )
      );

      if (!parentCircuit) {
        showNotification('Parent circuit not found. Please close and try again.', 'error');
        return;
      }

      const gateway = parentCircuit.components.find(c => c.instanceId === gwInstanceId);

      if (!gateway) {
        showNotification('Gateway component not found.', 'error');
        return;
      }

      console.log('✅ Found parent circuit:', parentCircuit.circuitId);

      const flowNodes = gateway.nodeRedFlow?.nodes || [];
      const flowWires = gateway.nodeRedFlow?.wires || [];

      // ✅ NEW: Enrich nodes from the component library exactly like handleLoadCircuit does
      const allLibraryComponents = Object.values(componentsByCategory).flat();

      const enrichedNodes = flowNodes.map(n => {
        const libraryComponent = allLibraryComponents.find(
          lib => lib.componentId === n.componentId
        );

        // ✅ NEW: Rebuild connectedTo on each pin from the wire list
        const pinDetailsWithConnections = (n.pinDetails || []).map(pin => {
          const asFrom = flowWires.find(
            w => w.from.nodeInstanceId === n.instanceId && w.from.portName === pin.pinName
          );
          const asTo = flowWires.find(
            w => w.to.nodeInstanceId === n.instanceId && w.to.portName === pin.pinName
          );
          return {
            ...pin,
            connectedTo: asFrom
              ? `${asFrom.to.nodeInstanceId}:${asFrom.to.portName}`
              : asTo
                ? `${asTo.from.nodeInstanceId}:${asTo.from.portName}`
                : pin.connectedTo || null,
            visible: pin.visible !== false,
          };
        });

        if (libraryComponent) {
          return {
            ...n,
            nodeRedType:        libraryComponent.nodeRedType || n.nodeRedType || null,
            name:               libraryComponent.name,
            needsConfiguration: libraryComponent.needsConfiguration,
            icon:               libraryComponent.icon,
            visual:             libraryComponent.visual,
            description:        libraryComponent.description,
            specifications:     libraryComponent.specifications,
            documentation:      libraryComponent.documentation,
            simulation:         libraryComponent.simulation,
            codeTemplate:       libraryComponent.codeTemplate,
            communicationCapabilities: libraryComponent.communicationCapabilities || [], // ✅ ADD THIS

            communicationConfigs: Object.keys(n.communicationConfigs || {}).length > 0
              ? n.communicationConfigs
              : libraryComponent.communicationConfigs,
            wirelessConfigs: Object.keys(n.wirelessConfigs || {}).length > 0
              ? n.wirelessConfigs
              : libraryComponent.wirelessConfigs,
            pinDetails: pinDetailsWithConnections.length > 0
              ? pinDetailsWithConnections
              : libraryComponent.pinDetails,

            componentName: n.componentName || libraryComponent.name,
          };
        }

        // Library component not found — use what was saved, still restore connections
        return {
          ...n,
          componentName: n.componentName || n.label,
          pinDetails: pinDetailsWithConnections,
        };
      });

      console.log('🔴 Enriched nodes nodeRedType check:',
        enrichedNodes.map(n => ({ id: n.instanceId, nodeRedType: n.nodeRedType }))
      );

      const syntheticCircuit = {
        _id:       parentCircuit._id,
        circuitId: parentCircuit.circuitId,
        studentId: parentCircuit.studentId,
        courseId:  parentCircuit.courseId,
        labId:     parentCircuit.labId,
        sessionId: flowSessionId,

        name:        `${gateway.label || gateway.componentName} — Flow`,
        description: `Node-RED flow for ${gateway.label || gateway.componentName}`,
        boardType:   parentCircuit.boardType,
        boardPosition: { x: 400, y: 300 },

        components: enrichedNodes, // ✅ use enriched nodes instead of raw flowNodes

        connections: flowWires.map(w => ({
          connectionId: w.wireId,
          wireType:     'nr_message',
          wireColor:    '#94a3b8',
          from: { componentInstanceId: w.from.nodeInstanceId, pinName: w.from.portName },
          to:   { componentInstanceId: w.to.nodeInstanceId,   pinName: w.to.portName },
          isValid: true
        })),

        canvasSettings: {
          zoom: 1, panX: 0, panY: 0,
          gridSize: 20, snapToGrid: true,
          showGrid: true, backgroundColor: '#fff8f0'
        },
        status:   'draft',
        isValid:  false,
        isSynced: false,

        _parentCircuit:     parentCircuit,
        _gatewayInstanceId: gwInstanceId,
      };

      setCircuit(syntheticCircuit);
      setCanvasSettings(syntheticCircuit.canvasSettings);
      setHasUnsavedChanges(false);

      const reEnrichedParentComponents = parentCircuit.components.map(c =>
        c.instanceId === gwInstanceId
        ? {
            ...c,
            nodeRedFlow: {
              ...gateway.nodeRedFlow,
              nodes: enrichedNodes.map(n => ({
                instanceId:    n.instanceId,
                componentId:   n.componentId,
                componentName: n.componentName,
                nodeRedType:   n.nodeRedType,   // ✅ now has the value from library
                category:      n.category,
                label:         n.label,
                enabled:       n.enabled ?? true,
                communicationCapabilities: n.communicationCapabilities || [],
                communicationConfigs: n.communicationConfigs || {},
                wirelessConfigs:    n.wirelessConfigs || {},
                pinDetails:    n.pinDetails || [],
                position:      n.position,
              })),
              wires: gateway.nodeRedFlow?.wires || [],
            }
          }
        : c
      );

    // Only write back if any node was missing nodeRedType (avoid unnecessary writes)
    const hadMissingNodeRedType = enrichedNodes.some(n => 
      !gateway.nodeRedFlow?.nodes?.find(orig => orig.instanceId === n.instanceId)?.nodeRedType
    );

    if (hadMissingNodeRedType && parentCircuit._id) {
      await updateCircuit(parentCircuit._id, {
        ...parentCircuit,
        components: reEnrichedParentComponents,
      });
      console.log('✅ Re-saved enriched nodeRedType values back to DB');
    }

      console.log('✅ Sub-canvas circuit loaded with',
        enrichedNodes.length, 'nodes and', flowWires.length, 'wires');

    } catch (err) {
      console.error('❌ Error loading sub-canvas:', err);
      showNotification('Failed to load Node-RED flow editor.', 'error');
    }
  };

  // Detect input and output node types from the current sub-canvas circuit
  // Only relevant when isSubCanvas === true
  const detectFlowNodeTypes = useCallback(() => {
    if (!circuit?.components) return { inputNodeType: null, outputNodeType: null };

    const inputNode = circuit.components.find(c =>
      c.category === 'nr_input' && c.enabled !== false
    );
    const outputNode = circuit.components.find(c =>
      c.category === 'nr_output' && c.enabled !== false
    );

    return {
      inputNodeType:  inputNode?.nodeRedType  ?? null,   // e.g. 'modbus-read'
      outputNodeType: outputNode?.nodeRedType ?? null,   // e.g. 'mqtt out'
      inputNodeName:  inputNode?.componentName  ?? null, // e.g. 'Modbus Read'
      outputNodeName: outputNode?.componentName ?? null, // e.g. 'MQTT Out'
      gatewayInProtocol:  inputNode?.communicationCapabilities?.[0] ?? null,
      gatewayOutProtocol: outputNode?.communicationCapabilities?.[0] ?? null,
    };
  }, [circuit?.components]);

  // ============================================================================
  // VALIDATION PANEL COMPONENT
  // ============================================================================

  const ConnectionErrorPanel = ({ errors, onRemove, onClearAll, hasPending, onAccept, onCancel }) => {
    if (errors.length === 0) return null;

    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    };

    return (
      <div className="validation-panel-overlay">
        <div className="validation-panel-content">

          <div className="validation-panel-header">
            <h3>⚠️ Connection Issues ({errors.length})</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {hasPending ? (
                <>
                  <button 
                    className="validation-panel-accept-btn"
                    onClick={onAccept}
                  >
                    ✅ Accept Anyway
                  </button>
                  <button 
                    className="validation-close-btn"
                    onClick={onCancel}
                  >
                    ✕
                  </button>
                </>
              ) : (
                <button 
                  className="validation-close-btn"
                  onClick={onClearAll}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="validation-panel-body">
            {errors.map((error) => (
              <div
                key={error.id}
                className={error.type === 'warning' ? 'validation-warning-item' : 'validation-error-item'}
              >
                <div className="validation-bullet">
                  {error.type === 'warning' ? '⚠️' : '❌'}
                </div>
                <div className="validation-message-text" style={{ flex: 1 }}>
                  <strong>{error.title}</strong>
                  {error.message && <div style={{ marginTop: '4px' }}>{error.message}</div>}
                  {(error.from || error.to) && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontFamily: 'monospace' }}>
                      {error.from && <div>From: {error.from}</div>}
                      {error.to && <div>To: {error.to}</div>}
                    </div>
                  )}
                  {error.details && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                      💡 {error.details}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                    {formatTime(error.timestamp)}
                  </div>
                </div>
                {!hasPending && (
                  <button
                    className="validation-close-btn"
                    onClick={() => onRemove(error.id)}
                    title="Dismiss"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  };

  // ✅ NEW: Handle validation error click - highlight component
  const handleValidationErrorClick = (error, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    console.log('🎯 Validation error clicked:', error);
    
    if (!error.component) {
      console.log('⚠️ No component ID in error');
      return;
    }
    
    const component = circuit?.components?.find(c => c.instanceId === error.component);
    
    if (!component) {
      console.log('⚠️ Component not found:', error.component);
      return;
    }
    
    console.log('✅ Highlighting component:', component.label || component.componentName);
    
    // ✅ Clear any existing timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      console.log('🧹 Cleared previous highlight timeout');
    }
    
    // ✅ NEW: Close validation panel so user can see the highlight
    setShowValidationPanel(false);
    
    setHighlightedComponent(error.component);
    console.log('✅ Set highlightedComponent to:', error.component);
    
    console.log('⚠️ Scroll disabled for testing');
    
    // ✅ Store timeout reference
    highlightTimeoutRef.current = setTimeout(() => {
      console.log('⏰ Removing highlight after 3s');
      setHighlightedComponent(null);
      highlightTimeoutRef.current = null;
    }, 5000);
  };

  const handleSaveSubCanvas = async () => {
    if (!circuit?._parentCircuit || !circuit?._gatewayInstanceId) {
      showNotification('Cannot save: parent circuit reference lost. Please close and reopen.', 'error');
      return;
    }

    const parentCircuit    = circuit._parentCircuit;
    const gwInstanceId     = circuit._gatewayInstanceId;

    // Translate canvas components/connections back to nodeRedFlow format
    const updatedFlow = {
      nodes: circuit.components.map(c => ({
        instanceId:    c.instanceId,
        componentId:   c.componentId,
        componentName: c.componentName,
        nodeRedType:   c.nodeRedType,
        category:      c.category,
        label:         c.label,
        icon:               c.icon,               
        needsConfiguration: c.needsConfiguration,
        enabled:       c.enabled ?? true,
        communicationCapabilities: c.communicationCapabilities || [],
        communicationConfigs: c.communicationConfigs || {},
        wirelessConfigs:    c.wirelessConfigs || {},
        pinDetails:    c.pinDetails || [],
        position:      c.position,
      })),
      wires: circuit.connections.map(conn => ({
        wireId:   conn.connectionId,
        wireType: 'nr_message',
        from: {
          nodeInstanceId: conn.from.componentInstanceId,
          portName:       conn.from.pinName,
        },
        to: {
          nodeInstanceId: conn.to.componentInstanceId,
          portName:       conn.to.pinName,
        },
      })),
      generatedAt: new Date(),
      flowLabel:       circuit.name,
      flowDescription: circuit.description,
    };

    // Patch the gateway component in the parent circuit
    const updatedParentComponents = parentCircuit.components.map(c =>
      c.instanceId === gwInstanceId
        ? { ...c, nodeRedFlow: updatedFlow }
        : c
    );

    const updatedParentCircuit = {
      ...parentCircuit,
      components: updatedParentComponents,
      isValid: false,    // parent needs re-validation after flow change
      isSynced: false,
    };

    const response = await updateCircuit(parentCircuit._id, updatedParentCircuit);

    if (response.success) {
      showNotification('Node-RED flow saved successfully!', 'success');
      setHasUnsavedChanges(false);
      setCircuit(prev => ({
        ...prev,
        _parentCircuit: updatedParentCircuit
      }));

      localStorage.setItem('NR_FLOW_SAVED', JSON.stringify({
        gatewayInstanceId: gwInstanceId,
        updatedFlow,
        timestamp: Date.now()
      }));
      // console.log('📦 localStorage set:', localStorage.getItem('NR_FLOW_SAVED'));
    } else {
      showNotification('Failed to save Node-RED flow.', 'error');
    }
  };

  // ============================================================================
  // PROFILE HEADER DATA
  // ============================================================================

  const profileHeaderData = {
    ...authData,
    // title: sessionId ? '🔧 IoT Circuit Builder' : '🎯 Practice Circuit Builder',
    // subTitle: labInfo?.title || courseInfo?.courseId || 'Circuit Design'
  };

  // ============================================================================
  // JSX RETURN - MAIN RENDER
  // ============================================================================

  return (
    <div className="main-container">
      <div className="main-content circuit-builder-container">
        {/* Loading Overlay while initializing */}
        {isInitializing && (
          <div className="loading-overlay">
            <div className="loader" />
            <p>Initializing circuit builder...</p>
          </div>
        )}

        {/* Header Section */}
        <div className="header-row">
          <ProfileHeader 
            data={profileHeaderData}
            activePage="circuits" 
          />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={5} icons={[]} />
        </div>

        <div className="page-title">
          <h3>{sessionId ? '🔧 IoT Circuit Builder' : '🎯 Practice Circuit Builder'}</h3>
          {/* <p>{labInfo?.title || courseInfo?.courseId || 'Circuit Design'}</p> */}
        </div>

        {/* Main Builder Layout */}
        <div className="circuit-builder-layout">
          
          {/* Left Panel - Component Library */}
          <div className="component-library-panel">
            <div className="component-library-header">
              <h3>📦 Component Library</h3>
              <button
                className="fullscreen-library-btn"
                onClick={handleOpenFullScreenLibrary}
                title="Open full-screen component library"
              >
                ⛶
              </button>
            </div>
            
            <div className="search-panel" style={{flexWrap: 'unset', position: 'relative'}}>
              <input
                type="text"
                className="component-search-input"
                placeholder="🔍 Search components..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                // style={{paddingRight: '5px', height: '30px'}} // Make room for the X button
              />
              {searchText && (
                <button
                  className="clear-search-btn"
                  // style={{position: 'absolute', width: '30px', height: '30px', border: 'aqua', top: '50%', transform: 'translateY(-50%)'}}
                  onClick={() => setSearchText('')}
                  title="Clear search"
                >
                  ✖
                </button>
              )}
            </div>

            {authData.role === 'admin' && (
              <div className="panel-header">
                <button
                  className="add-component-btn bulk-import-btn"
                  onClick={handleOpenBulkImportModal}
                  title="Bulk import components by category"
                >
                  📥
                </button>
                <h3>Bulk Upload</h3>  
              </div>
            )}

            {/* Component Library Loading/Error States */}
            {libraryLoading ? (
                <div className="component-library-loading">
                    <div className="loader" />
                    <p>Loading components...</p>
                </div>
            ) : !componentsByCategory ? (
                <div className="component-library-loading">
                    <p>⚠️ Component library not loaded</p>
                    <button onClick={() => getAllComponents()}>🔄 Reload Library</button>
                </div>
            ) : (
                <div className="component-categories">
                    {CourseDomains[domainName]?.canvases
                        .flatMap(canvas => canvas.categories)
                        .map(category => (
                            <ComponentCategoryList
                                key={category.key}
                                categoryKey={category.key}
                                categoryName={category.label}
                                categoryIcon={category.label.split(' ')[0]}
                                domain={domainName}
                                components={componentsByCategory[category.key]?.filter(comp =>
                                    !comp.subCanvasOnly &&
                                    comp.name.toLowerCase().includes(searchText.toLowerCase())
                                )}
                                isExpanded={expandedCategories[category.key]}
                                onToggle={() => toggleCategory(category.key)}
                                onDragStart={handleDragStart}
                                onViewComponent={handleViewLibraryComponent}
                                onShowInfo={handleShowComponentInfo}
                                onConfigureComponent={handleConfigureComponent}
                                showViewButton={(authData.role === 'instructor' || authData.role === 'admin')}
                                courseTitle={courseInfo?.title}
                                userRole={authData.role}
                            />
                        ))
                    }
                </div>
            )}
          </div>

          {/* Canvas Area */}
          <div className="canvas-area">
            <div className="canvas-header">
              {/* Canvas header title */}
              <h3>
                {isSubCanvas
                  ? '🔴 Node-RED Flow Canvas'
                  : '🖼️ Circuit Canvas'}
              </h3>
              
              <div className="name-group">
                <label>{isSubCanvas ? 'Flow Name:' : 'Circuit Name:'}</label>
                <input
                  type="text"
                  value={circuit?.name || ""}
                  onChange={(e) => handleUpdateCircuit("name", e.target.value)}
                  placeholder={isSubCanvas ? 'Flow Name:' : "Circuit Name"}
                  className="name-input"
                />
              </div>

              <div className="canvas-controls">
                <button 
                  className={`canvas-control-btn ${connectionMode ? 'active' : ''}`}
                  onClick={() => {
                    setConnectionMode(!connectionMode);
                    if (connectionMode) {
                      handleCancelConnection();
                    }
                  }}
                >
                  {connectionMode ? '🔗 Connecting...' : '🔗 Connect Pins'}
                </button>
                
                {connectionStart && (
                  <button 
                    className="canvas-control-btn cancel-btn"
                    onClick={handleCancelConnection}
                  >
                    ❌ Cancel Connection
                  </button>
                )}
                
                <button 
                  className="canvas-control-btn"
                  onClick={() => setCanvasSettings(prev => ({ ...prev, showGrid: !prev.showGrid }))}
                >
                  {canvasSettings.showGrid ? '🔲 Hide Grid' : '⬜ Show Grid'}
                </button>
                
                <button 
                  className="canvas-control-btn"
                  onClick={() => setCanvasSettings(prev => ({ ...prev, snapToGrid: !prev.snapToGrid }))}
                >
                  {canvasSettings.snapToGrid ? '🧲 Snap: ON' : '🧲 Snap: OFF'}
                </button>
              </div>
            </div>

            <SplitCanvasArea
              circuit={circuit}
              domain={domainName}
              canvasSettings={canvasSettings}
              selectedComponent={selectedComponent}
              selectedConnection={selectedConnection}
              connectionMode={connectionMode}
              connectionStart={connectionStart}
              tempConnection={tempConnection}
              highlightedComponent={highlightedComponent} 
              hardwareCanvasRef={hardwareCanvasRef}
              bridgeCanvasRef={bridgeCanvasRef}
              cloudCanvasRef={cloudCanvasRef}
              handleDrop={handleDrop}
              handleDragOver={handleDragOver}
              handleCanvasMouseMove={handleCanvasMouseMove}
              handleCancelConnection={handleCancelConnection}
              handleSelectComponent={handleSelectComponent}
              handleComponentDragStart={handleComponentDragStart}
              handleComponentDragEnd={handleComponentDragEnd}
              handleStartConnection={handleStartConnection}
              handleCompleteConnection={handleCompleteConnection}
              handleDeleteConnection={handleDeleteConnection}
              setPropertiesModalVisible={setPropertiesModalVisible}
              setWirePropertiesModalVisible={setWirePropertiesModalVisible}
              setSelectedConnection={setSelectedConnection}
              setAlertConfig={setAlertConfig}
              setAlertVisible={setAlertVisible}
              getPinPosition={getPinPosition}
              getCrossCanvasPosition={getCrossCanvasPosition}
              onOpenFlowEditor={handleOpenFlowEditor}
            />

          </div>

        </div>

        {/* Connection Error Panel */}
        {connectionErrors.length > 0 && (
          <ConnectionErrorPanel 
            errors={connectionErrors}
            onRemove={removeConnectionError}
            onClearAll={clearAllErrors}
            hasPending={!!pendingConnection}
            onAccept={handleAcceptPendingConnection}
            onCancel={handleCancelPendingConnection}
          />
        )}

        {/* Bottom - Action Buttons */}
        <div className="circuit-actions-bar">

          <CustomButton
            buttonTitle={saveButtonTitle}
            action={handleSaveAndValidate}
            bgColor={saveButtonColor}
            disabled={!circuit}
          />

          {/* Hide Sync and Simulate in sub-canvas — these belong to the parent */}
          {!isSubCanvas && (
            <>
              <CustomButton
                buttonTitle="🔄 2. Sync to Simulator"
                action={handleSyncToSimulator}
                bgColor="#8b5cf6"
                disabled={!circuit?.isValid}
              />
              <CustomButton
                buttonTitle={simulatorLabel}
                action={handleRunSimulator}
                bgColor="#f59e0b"
                disabled={!circuit?.sessionId || !circuit?.isSynced}
              />
            </>
          )}

        </div>

      </div>

      {showValidationPanel && validationResults && (
        <div className="validation-panel-overlay">
          <div className="validation-panel-content">
            <div className="validation-panel-header">
              <h3>
                {validationResults.valid ? '✅ Validation Passed' : '❌ Validation Failed'}
              </h3>
              <button
                className="validation-close-btn"
                onClick={() => setShowValidationPanel(false)}
              >
                ✖
              </button>
            </div>

            {/* ✅ NEW: Category Filter Buttons */}
            {/* {!validationResults.valid && categorizedValidation && ( */}
            {((validationResults.errors && validationResults.errors.length > 0) || 
              (validationResults.warnings && validationResults.warnings.length > 0)) && 
              categorizedValidation && (
              <div className="validation-filters">
                  {/* All tab — always shown */}
                  <button
                      className={`filter-btn ${validationFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setValidationFilter('all')}
                  >
                      All ({(validationResults.errors?.length || 0) + (validationResults.warnings?.length || 0)})
                  </button>

                  {/* Dynamic filters from CourseDomains */}
                  {domainValidation.filters.map(filter => {
                      const categoryData = categorizedValidation?.[filter.key];
                      const count = (categoryData?.errors?.length || 0) + (categoryData?.warnings?.length || 0);
                      return (
                          <button
                              key={filter.key}
                              className={`filter-btn ${validationFilter === filter.key ? 'active' : ''}`}
                              onClick={() => setValidationFilter(filter.key)}
                              disabled={count === 0}
                          >
                              {filter.label} ({count})
                          </button>
                      );
                  })}
              </div>
            )}

            <div className="validation-panel-body">
              {/* {validationResults.errors && validationResults.errors.length > 0 ? ( */}
              {(validationResults.errors?.length > 0 || validationResults.warnings?.length > 0) ? (
                <div className="validation-errors">
                  <h4>⚠️ Issues Found:</h4>
                  {/* ✅ UPDATED: Show filtered errors based on selected category */}
                  {(() => {
                    let errorsToShow = validationResults.errors;
                    let warningsToShow = validationResults.warnings || [];

                    // Filter based on selected category
                    if (validationFilter !== 'all' && categorizedValidation) {
                      // Dynamic filter — works for any filter key from CourseDomains
                      const categoryData = categorizedValidation?.[validationFilter];
                      errorsToShow = categoryData?.errors || [];
                      warningsToShow = categoryData?.warnings || [];
                    }

                    return (
                      <>
                        {/* Show Errors */}
                        {errorsToShow.map((error, idx) => (
                          <div 
                            key={`error-${idx}`} 
                            className={`validation-item ${error.type} ${error.component ? 'clickable' : ''}`}
                            onClick={(e) => error.component && handleValidationErrorClick(error, e)}
                            style={{ cursor: error.component ? 'pointer' : 'default' }}
                          >
                            <strong>
                              {error.type === 'error' ? '❌' : '⚠️'} {error.type?.toUpperCase() || 'ERROR'}:
                            </strong>
                            <p>{error.message}</p>
                            {error.component && (
                              <small className="validation-component-hint">🎯 Click to highlight component</small>
                            )}
                            {error.solution && (
                              <small className="validation-solution">💡 {error.solution}</small>
                            )}
                          </div>
                        ))}

                        {/* Show Warnings */}
                        {warningsToShow.map((warning, idx) => (
                          <div 
                            key={`warning-${idx}`} 
                            className={`validation-item warning ${warning.component ? 'clickable' : ''}`}
                            onClick={(e) => warning.component && handleValidationErrorClick(warning, e)}
                            style={{ cursor: warning.component ? 'pointer' : 'default' }}
                          >
                            <strong>⚠️ WARNING:</strong>
                            <p>{warning.message}</p>
                            {warning.component && (
                              <small className="validation-component-hint">🎯 Click to highlight component</small>
                            )}
                            {warning.suggestion && (
                              <small className="validation-solution">💡 {warning.suggestion}</small>
                            )}
                          </div>
                        ))}

                        {/* Show message if no errors in this category */}
                        {errorsToShow.length === 0 && warningsToShow.length === 0 && validationFilter !== 'all' && (
                          <div className="validation-empty">
                            <p>✅ No {validationFilter} errors found</p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="validation-success">
                    {domainValidation.successMessages.map((msg, idx) => (
                        <p key={idx}>{msg}</p>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Config Validation Panel */}
      {showConfigValidationPanel && configValidationResults && (
        <div className="validation-panel-overlay">
          <div className="validation-panel-content">
            <div className="validation-panel-header">
              <h3>
                {configValidationResults.hasErrors ? '❌ Configuration Errors' : '⚠️ Configuration Warnings'}
              </h3>
              {configValidationResults.hasErrors && (
                <button
                  className="validation-close-btn"
                  onClick={() => setShowConfigValidationPanel(false)}
                >
                  ✖
                </button>
              )}
            </div>

            <div className="validation-panel-body">
              {configValidationResults.errors && configValidationResults.errors.length > 0 && (
                <div className="validation-errors">
                  <h4>⛔ Errors (must fix before saving):</h4>
                  {configValidationResults.errors.map((error, idx) => (
                    <div key={idx} className="validation-item error">
                      <p>❌ {error}</p>
                    </div>
                  ))}
                </div>
              )}

              {configValidationResults.warnings && configValidationResults.warnings.length > 0 && (
                <div className="validation-warnings">
                  <h4>⚠️ Warnings (recommended to fix):</h4>
                  {configValidationResults.warnings.map((warning, idx) => (
                    <div key={idx} className="validation-item warning">
                      <p><strong>{warning.protocol}:</strong> {warning.message}</p>
                      {warning.suggestion && (
                        <p className="warning-suggestion">💡 {warning.suggestion}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!configValidationResults.hasErrors && (
              <div className="validation-panel-footer">
                <button
                  className="config-cancel-btn"
                  onClick={() => {
                    setShowConfigValidationPanel(false);
                    setConfigModalVisible(true);
                  }}
                >
                  ← Go Back to Config
                </button>
                <button
                  className="config-save-btn"
                  onClick={() => {
                    setShowConfigValidationPanel(false);
                    setConfigValidationResults(null);
                  }}
                >
                  ✓ Ignore Warnings
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wire Properties Modal */}
      {wirePropertiesModalVisible && selectedConnection && (
        <div className="modal-overlay">
          <div className="modal-content-top" style={{maxWidth: '70%'}}>
            <div className="modal-header-top">
              <h3>🔌 Wire Properties</h3>
              {/* Action Buttons */}
              <div className="modal-actions-group">
                <button
                  className="modal-general-btn"
                  onClick={() => {
                    // handleSaveCircuit();
                    handleSaveAndValidate();
                    setWirePropertiesModalVisible(false);
                    setSelectedConnection(null);
                  }}
                >
                  💾
                </button> 
                <button
                  className="modal-general-btn"
                  onClick={() => {
                    setWirePropertiesModalVisible(false);
                    setAlertConfig({
                      title: 'Delete Wire',
                      icon: '🗑️',
                      message: `Delete this connection?`,
                      onConfirm: () => {
                        handleDeleteConnection();
                        setAlertVisible(false);
                      }
                    });
                    setAlertVisible(true);
                  }}
                >
                  🗑️
                </button>            
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setWirePropertiesModalVisible(false);
                    setSelectedConnection(null);
                  }}
                >
                  ✖
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="properties-content">
                
                {/* Connection Info */}
                <div className="property-group">
                  <label>From:</label>
                  <p className="property-value">
                    {circuit.components.find(c => c.instanceId === selectedConnection.from.componentInstanceId)?.label} - {selectedConnection.from.pinName}
                  </p>
                </div>

                <div className="property-group">
                  <label>To:</label>
                  <p className="property-value">
                    {circuit.components.find(c => c.instanceId === selectedConnection.to.componentInstanceId)?.label} - {selectedConnection.to.pinName}
                  </p>
                </div>

                {/* Wire Type */}
                <div className="property-group">
                  <label>Wire Type:</label>
                  <select
                      value={selectedConnection.wireType || domainWireTypes[0]?.value}
                      onChange={(e) => handleUpdateWireProperty('wireType', e.target.value)}
                      className="property-input"
                  >
                      {domainWireTypes.map(type => (
                          <option key={type.value} value={type.value}>
                              {type.label}
                          </option>
                      ))}
                  </select>
                </div>

                {/* Wire Color */}
                <div className="property-group">
                  <label>Wire Color:</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={selectedConnection.wireColor || '#000000'}
                      onChange={(e) => handleUpdateWireProperty('wireColor', e.target.value)}
                      className="property-input"
                      style={{ width: '60px', height: '40px' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      {selectedConnection.wireColor || '#000000'}
                    </span>
                  </div>
                </div>

                {/* Wire Thickness */}
                <div className="property-group">
                  <label>Wire Thickness:</label>
                  <select
                    value={selectedConnection.strokeWidth || 3}
                    onChange={(e) => handleUpdateWireProperty('strokeWidth', parseInt(e.target.value))}
                    className="property-input"
                  >
                    <option value="2">Thin (2px) - Signal</option>
                    <option value="3">Medium (3px) - Default</option>
                    <option value="4">Thick (4px) - Power</option>
                    <option value="5">Extra Thick (5px)</option>
                  </select>
                </div>

                {/* Wire Label */}
                <div className="property-group">
                  <label>Label (Optional):</label>
                  <input
                    type="text"
                    value={selectedConnection.label || ''}
                    onChange={(e) => handleUpdateWireProperty('label', e.target.value)}
                    placeholder="e.g., Power Supply"
                    className="property-input"
                  />
                </div>

                {/* Notes */}
                <div className="property-group">
                  <label>Notes (Optional):</label>
                  <textarea
                    value={selectedConnection.notes || ''}
                    onChange={(e) => handleUpdateWireProperty('notes', e.target.value)}
                    placeholder="Add notes about this connection..."
                    className="property-input"
                    rows="3"
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Properties Modal */}
      {propertiesModalVisible && selectedComponent && (
        <div className="modal-overlay">
          <div className="modal-content-top" style={{maxWidth: '60%'}}>
            <div className="modal-header-top">
              <h3>⚙️ Component Properties</h3>
              <div className="modal-actions-group">
                {hasUnsavedProperties && (
                  <button
                    className="modal-general-btn"
                    onClick={handleSaveComponentProperties}
                    title="Save changes"
                  >
                    💾
                  </button>
                )}
                {/* Configure Button */}
                {selectedComponent.needsConfiguration && (
                <button
                    className="modal-general-btn"
                    onClick={handleOpenConfigModal}
                    title={domainButtonTooltip}
                >
                    ⚙️
                </button>
                )}
                {/* Delete button */}
                <button
                  className="modal-general-btn"
                  onClick={handleDeleteComponent}
                  title="Delete Component"
                >
                  🗑️
                </button> 
                {/* cancel button */}
                <button
                  className="modal-close-btn"
                  onClick={handleCancelPropertyChanges}
                  title="Close"
                >
                  ✖
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="properties-content">
                <div className="property-group">
                  <label>Component:</label>
                  <p className="property-value">{selectedComponent.componentName}</p>
                </div>

                <div className="property-group">
                  <label>Instance ID:</label>
                  <p className="property-value">{selectedComponent.instanceId}</p>
                </div>

                <div className="property-group">
                  <label>Label:</label>
                  <input
                    type="text"
                    value={selectedComponent.label || ''}
                    onChange={(e) => handleUpdateComponentProperty('label', e.target.value)}
                    className="property-input"
                  />
                </div>

                <div className="property-group">
                  <label>Position X:</label>
                  <input
                    type="number"
                    value={selectedComponent.position?.x || 0}
                    onChange={(e) => handleUpdateComponentProperty('position', { 
                      ...selectedComponent.position, 
                      x: parseInt(e.target.value) 
                    })}
                    className="property-input"
                  />
                </div>

                <div className="property-group">
                  <label>Position Y:</label>
                  <input
                    type="number"
                    value={selectedComponent.position?.y || 0}
                    onChange={(e) => handleUpdateComponentProperty('position', { 
                      ...selectedComponent.position, 
                      y: parseInt(e.target.value) 
                    })}
                    className="property-input"
                  />
                </div>

                <div className="property-group">
                  <label>Pins:</label>
                  <div className="pins-list">
                    {/* {selectedComponent.pins?.map((pin, idx) => ( */}
                    {selectedComponent.pinDetails?.map((pin, idx) => (
                      <div key={idx} className="pin-item pin-visibility-item">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={pin.visible !== false}
                            onChange={(e) => {
                              // const updatedPins = [...selectedComponent.pins];
                              const updatedPins = [...selectedComponent.pinDetails];
                              updatedPins[idx] = { ...updatedPins[idx], visible: e.target.checked };
                              // handleUpdateComponentProperty('pins', updatedPins);
                              handleUpdateComponentProperty('pinDetails', updatedPins);
                            }}
                            className="pin-visibility-checkbox"
                          />
                          <span>{pin.pinName}: {pin.description}</span>
                        </div>
                        {pin.connectedTo && <span className="pin-connected">🔗</span>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Toggle All Pins Button */}
                <div className="property-group">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="property-action-btn"
                      onClick={() => {
                        // const updatedPins = selectedComponent.pins.map(p => ({ ...p, visible: true }));
                        const updatedPins = selectedComponent.pinDetails.map(p => ({ ...p, visible: true }));
                        // handleUpdateComponentProperty('pins', updatedPins);
                        handleUpdateComponentProperty('pinDetails', updatedPins);
                      }}
                    >
                      ✅ Show All Pins
                    </button>
                    <button
                      className="property-action-btn"
                      onClick={() => {
                        // const updatedPins = selectedComponent.pins.map(p => ({ ...p, visible: false }));
                        const updatedPins = selectedComponent.pinDetails.map(p => ({ ...p, visible: false }));
                        // handleUpdateComponentProperty('pins', updatedPins);
                        handleUpdateComponentProperty('pinDetails', updatedPins);
                      }}
                    >
                      ❌ Hide All Pins
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Configuration Modal */}
      <ComponentConfiguration
        visible={configModalVisible}
        component={selectedConfigComponent}
        domain={domainName}
        initialConfig={componentConfig}
        circuit={circuit}
        onSave={handleSaveConfig}
        onClose={() => setConfigModalVisible(false)}
        onShowValidationPanel={(validationData) => {
          setConfigValidationResults(validationData);
          setShowConfigValidationPanel(true);
        }}
        onOpenFlowEditor={
          selectedConfigComponent?.isFlowEngine
            ? () => {
                setSelectedGatewayComponent(selectedConfigComponent);
                setFlowEditorVisible(true);
              }
            : undefined
        }
        flowNodeTypes={isSubCanvas ? detectFlowNodeTypes() : null}
        gatewayLabel={circuit?._parentCircuit?.components?.find(c => c.isFlowEngine)?.label ?? null}
      />

      {/* Add Component Modal (Instructor Only) */}
      {addComponentModalVisible && (
        <div className="modal-overlay">
          <div className="modal-content properties-modal add-component-modal">
            <div className="modal-header">
              <h3>➕ Add New Component to Library</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setAddComponentModalVisible(false);
                  setNewComponentData({
                    name: '',
                    category: 'sensors',
                    needsConfiguration: false,
                    icon: '📦',
                    pinDetails: '',
                    description: '',
                    specifications: {},
                    simulation: null,
                    codeTemplate: null,
                    documentation: null,
                    createdBy: ''
                  });
                }}
              >
                ✖
              </button>
            </div>

            <div className="modal-body">
              <div className="properties-content">
                
                {/* Component Name */}
                <div className="property-group">
                  <label>Component Name *</label>
                  <input
                    type="text"
                    value={newComponentData.name}
                    onChange={(e) => setNewComponentData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., DHT22, BH1750, LED"
                    className="property-input"
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    Name should be clear and descriptive
                  </small>
                </div>

                {/* Category */}
                <div className="property-group">
                  <label>Category *</label>
                  <select
                    value={newComponentData.category}
                    onChange={(e) => setNewComponentData(prev => ({ ...prev, category: e.target.value }))}
                    className="property-input"
                  >
                    {CourseDomains[domainName]?.canvases.flatMap(canvas =>
                        canvas.categories.map(cat => (
                            <option key={cat.key} value={cat.key}>
                                {cat.label}
                            </option>
                        ))
                    )}

                  </select>
                </div>

                {/* Icon */}
                <div className="property-group">
                  <label>Icon (Emoji) *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={newComponentData.icon}
                      onChange={(e) => setNewComponentData(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="e.g., 🌡️"
                      className="property-input"
                      maxLength={4}
                      style={{ width: '80px', fontSize: '1.5rem', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '2rem' }}>{newComponentData.icon}</span>
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    Pick an emoji that represents the component
                  </small>
                </div>

                {/* Pins */}
                <div className="property-group">
                  <label>Pins (comma-separated) *</label>
                  <input
                    type="text"
                    // value={newComponentData.pins}
                    value={newComponentData.pinDetails}
                    // onChange={(e) => setNewComponentData(prev => ({ ...prev, pins: e.target.value }))}
                    onChange={(e) => setNewComponentData(prev => ({ ...prev, pinDetails: e.target.value }))}
                    placeholder="e.g., VCC, GND, DATA or GPIO4, GPIO5, GPIO18"
                    className="property-input"
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    Enter pin names separated by commas (e.g., VCC, GND, DATA)
                  </small>
                </div>

                {/* Description */}
                <div className="property-group">
                  <label>Description</label>
                  <textarea
                    value={newComponentData.description}
                    onChange={(e) => setNewComponentData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of what this component does..."
                    className="property-input"
                    rows="3"
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {/* Specifications (Optional) */}
                <div className="property-group">
                  <label>Specifications (Optional - JSON format)</label>
                  <textarea
                    value={typeof newComponentData.specifications === 'string' 
                      ? newComponentData.specifications 
                      : JSON.stringify(newComponentData.specifications, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setNewComponentData(prev => ({ ...prev, specifications: parsed }));
                      } catch {
                        setNewComponentData(prev => ({ ...prev, specifications: e.target.value }));
                      }
                    }}
                    placeholder='{"voltage": "3.3V-5V", "current": "20mA"}'
                    className="property-input"
                    rows="4"
                    style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    Add technical specs in JSON format (optional)
                  </small>
                </div>

                {/* Divider */}
                <div style={{ 
                  borderTop: '1px solid #e5e7eb', 
                  margin: '1rem 0',
                  paddingTop: '1rem'
                }}>
                  <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    ℹ️ Advanced fields (simulation, codeTemplate, documentation) can be added later via database or bulk import
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <button
                    className="custom-button"
                    onClick={handleCreateLibraryComponent}
                    // disabled={!newComponentData.name || !newComponentData.category || !newComponentData.pins}
                    disabled={!newComponentData.name || !newComponentData.category || !newComponentData.pinDetails}
                    style={{
                      // backgroundColor: (!newComponentData.name || !newComponentData.category || !newComponentData.pins) 
                      backgroundColor: (!newComponentData.name || !newComponentData.category || !newComponentData.pinDetails) 
                        ? '#9ca3af' 
                        : '#10b981',
                      padding: '0.65rem 0.9rem',
                      // cursor: (!newComponentData.name || !newComponentData.category || !newComponentData.pins) 
                      cursor: (!newComponentData.name || !newComponentData.category || !newComponentData.pinDetails) 
                        ? 'not-allowed' 
                        : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      flex: 1
                    }}
                  >
                    ✅ Add Component
                  </button>
                  <button
                    className="custom-button"
                    onClick={() => {
                      setAddComponentModalVisible(false);
                      setNewComponentData({
                        name: '',
                        category: 'sensors',
                        needsConfiguration: false,
                        icon: '📦',
                        pinsDetail: '',
                        description: '',
                        specifications: {},
                        simulation: null,
                        codeTemplate: null,
                        documentation: null
                      });
                    }}
                    style={{
                      backgroundColor: '#6b7280',
                      padding: '0.65rem 0.91rem',
                    }}
                  >
                    ❌ Cancel
                  </button>
                </div>

                {/* Help Section */}
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '0.5rem',
                  fontSize: '0.8rem', 
                  color: '#4b5563',
                  lineHeight: '1.4'
                }}>
                  <strong>💡 Quick Tips:</strong>
                  <ul style={{ marginTop: '0.25rem', paddingLeft: '1.25rem', marginBottom: 0 }}>
                    <li>Name: Use official component names (DHT22, not "temp sensor")</li>
                    <li>Category: Choose the most appropriate category</li>
                    <li>Pins: Match exact pin names from datasheet</li>
                    <li>Icon: Use relevant emoji for quick recognition</li>
                  </ul>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal (Instructor Only) */}
      {bulkImportModalVisible && (
        <div className="modal-overlay">
          <div className="modal-content-top" style={{maxWidth: '70%'}}>
            <div className="modal-header-top">
              <h3>📥 Bulk Import Components</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setBulkImportModalVisible(false);
                  setSelectedCategory('');
                  setDuplicateCheckResult(null);
                  setShowDuplicateWarning(false);
                  clearImportData();
                }}
              >
                ✖
              </button>
            </div>

            <div className="modal-body">
              <div className="properties-content">
                
                {/* Category Selection */}
                <div className="property-group">
                  <label>Select Category to Import *</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setDuplicateCheckResult(null);
                      setShowDuplicateWarning(false);
                    }}
                    className="property-input"
                    disabled={bulkImport.isChecking || bulkImport.isImporting}
                  >
                    {CourseDomains[domainName]?.canvases.flatMap(canvas =>
                        canvas.categories.map(cat => (
                            <option key={cat.key} value={cat.key}>
                                {cat.label}
                            </option>
                        ))
                    )}
                  </select>
                </div>

                {/* Info Message */}
                <div className="property-group">
                  <div style={{ 
                    padding: '0.75rem', 
                    backgroundColor: '#dbeafe', 
                    border: '1px solid #3b82f6',
                    borderRadius: '0.5rem',
                    fontSize: '0.85rem',
                    color: '#1e40af'
                  }}>
                    ℹ️ This will import components from the backend library file into the database.
                  </div>
                </div>

                {/* Duplicate Warning */}
                {showDuplicateWarning && duplicateCheckResult && (
                  <div className="property-group">
                    <div style={{ 
                      padding: '0.75rem', 
                      backgroundColor: '#fef3c7', 
                      border: '1px solid #f59e0b',
                      borderRadius: '0.5rem',
                      fontSize: '0.85rem',
                      color: '#92400e'
                    }}>
                      <strong>⚠️ Duplicates Found</strong>
                      <p style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                        {duplicateCheckResult.duplicates.length} component(s) already exist in the database:
                      </p>
                      <div style={{ 
                        maxHeight: '150px', 
                        overflowY: 'auto',
                        backgroundColor: '#fffbeb',
                        padding: '0.5rem',
                        borderRadius: '0.25rem'
                      }}>
                        {duplicateCheckResult.duplicates.map((dup, idx) => (
                          <div key={idx} style={{ padding: '0.25rem 0' }}>
                            • {dup.name} ({dup.componentId})
                          </div>
                        ))}
                      </div>
                      <p style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                        Choose an option below:
                      </p>
                    </div>
                  </div>
                )}

                {/* Import Result */}
                {bulkImport.importResult && (
                  <div className="property-group">
                    <div style={{ 
                      padding: '0.75rem', 
                      backgroundColor: bulkImport.importResult.success ? '#d1fae5' : '#fee2e2',
                      border: `1px solid ${bulkImport.importResult.success ? '#10b981' : '#ef4444'}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.85rem'
                    }}>
                      <strong>
                        {bulkImport.importResult.success ? '✅ Import Complete' : '⚠️ Import Completed with Errors'}
                      </strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        <div>📦 Total: {bulkImport.importResult.total}</div>
                        <div>✅ Imported: {bulkImport.importResult.imported}</div>
                        <div>🔄 Updated: {bulkImport.importResult.updated}</div>
                        <div>⏭️ Skipped: {bulkImport.importResult.skipped}</div>
                        {bulkImport.importResult.errors.length > 0 && (
                          <div style={{ color: '#dc2626' }}>
                            ❌ Errors: {bulkImport.importResult.errors.length}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexDirection: 'column' }}>
                  
                  {/* Check/Import Buttons */}
                  {!showDuplicateWarning ? (
                    <button
                      className="custom-button"
                      onClick={handleCheckDuplicates}
                      disabled={!selectedCategory || bulkImport.isChecking || bulkImport.isImporting}
                      style={{
                        backgroundColor: !selectedCategory || bulkImport.isChecking || bulkImport.isImporting ? '#9ca3af' : '#3b82f6',
                        padding: '0.65rem 0.9rem',
                        cursor: !selectedCategory || bulkImport.isChecking || bulkImport.isImporting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {bulkImport.isChecking ? '🔄 Checking...' : bulkImport.isImporting ? '📥 Importing...' : '📥 Import Components'}
                    </button>
                  ) : (
                    /* Duplicate Resolution Buttons */
                    <>
                      <button
                        className="custom-button"
                        onClick={() => handleBulkImport(false)}
                        disabled={bulkImport.isImporting}
                        style={{
                          backgroundColor: bulkImport.isImporting ? '#9ca3af' : '#10b981',
                          padding: '0.65rem 0.9rem',
                          cursor: bulkImport.isImporting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {bulkImport.isImporting ? '📥 Importing...' : '⏭️ Skip Duplicates & Import New'}
                      </button>
                      
                      <button
                        className="custom-button"
                        onClick={() => handleBulkImport(true)}
                        disabled={bulkImport.isImporting}
                        style={{
                          backgroundColor: bulkImport.isImporting ? '#9ca3af' : '#f59e0b',
                          padding: '0.65rem 0.9rem',
                          cursor: bulkImport.isImporting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {bulkImport.isImporting ? '📥 Importing...' : '🔄 Update Existing & Import New'}
                      </button>
                    </>
                  )}

                  {/* Cancel Button */}
                  <button
                    className="custom-button"
                    onClick={() => {
                      setBulkImportModalVisible(false);
                      setSelectedCategory('');
                      setDuplicateCheckResult(null);
                      setShowDuplicateWarning(false);
                      clearImportData();
                    }}
                    disabled={bulkImport.isChecking || bulkImport.isImporting}
                    style={{
                      backgroundColor: '#6b7280',
                      padding: '0.65rem 0.9rem',
                      cursor: bulkImport.isChecking || bulkImport.isImporting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ❌ Cancel
                  </button>
                </div>

                {/* Help Text */}
                <div style={{ 
                  marginTop: '1rem', 
                  fontSize: '0.8rem', 
                  color: '#6b7280',
                  lineHeight: '1.4'
                }}>
                  <strong>💡 Tips:</strong>
                  <ul style={{ marginTop: '0.25rem', paddingLeft: '1.25rem' }}>
                    <li>Select a specific category or "All Categories" to import</li>
                    <li>Existing components will be detected automatically</li>
                    <li>"Skip Duplicates" will only import new components</li>
                    <li>"Update Existing" will replace duplicate components with new data</li>
                  </ul>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Component Modal */}
      {viewComponentModalVisible && selectedLibraryComponent && (
        <div className="modal-overlay">
          <div className="modal-content-top">
            <div className="modal-header-top">
              <h3>
                {isEditingLibraryComponent ? '✏️ Edit Component' : '👁️ Component Details'}
              </h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setViewComponentModalVisible(false);
                  setSelectedLibraryComponent(null);
                  setIsEditingLibraryComponent(false);
                }}
              >
                ✖
              </button>
            </div>

            <div className="modal-body">
              <div className="properties-content">
                
                {/* Component ID (Read-only) */}
                <div className="property-group">
                  <label>Component ID</label>
                  <input
                    type="text"
                    value={selectedLibraryComponent.componentId}
                    readOnly
                    className="property-input"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                {/* Component Name */}
                <div className="property-group">
                  <label>Component Name *</label>
                  <input
                    type="text"
                    value={editComponentData.name}
                    onChange={(e) => setEditComponentData(prev => ({ ...prev, name: e.target.value }))}
                    readOnly={!isEditingLibraryComponent}
                    className="property-input"
                    style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                  />
                </div>

                {/* Category */}
                <div className="property-group">
                  <label>Category *</label>
                  <select
                    value={editComponentData.category}
                    onChange={(e) => setEditComponentData(prev => ({ ...prev, category: e.target.value }))}
                    disabled={!isEditingLibraryComponent}
                    className="property-input"
                    style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                  >
                    {CourseDomains[domainName]?.canvases.flatMap(canvas =>
                        canvas.categories.map(cat => (
                            <option key={cat.key} value={cat.key}>
                                {cat.label}
                            </option>
                        ))
                    )}
                  </select>
                </div>

                {/* Icon */}
                <div className="property-group">
                  <label>Icon *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={editComponentData.icon}
                      onChange={(e) => setEditComponentData(prev => ({ ...prev, icon: e.target.value }))}
                      readOnly={!isEditingLibraryComponent}
                      className="property-input"
                      style={{
                        width: '80px',
                        fontSize: '1.5rem',
                        textAlign: 'center',
                        ...((!isEditingLibraryComponent) && { backgroundColor: '#f9fafb' })
                      }}
                      maxLength={4}
                    />
                    <span style={{ fontSize: '2rem' }}>{editComponentData.icon}</span>
                  </div>
                </div>

                {/* Pins */}
                <div className="property-group">
                  <label>Pins *</label>
                  <input
                    type="text"
                    // value={editComponentData.pins}
                    value={editComponentData.pinDetails}
                    onChange={(e) => setEditComponentData(prev => ({ ...prev, pinDetails: e.target.value }))}
                    readOnly={!isEditingLibraryComponent}
                    className="property-input"
                    placeholder="e.g., VCC, GND, DATA"
                    style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    {/* {Array.isArray(selectedLibraryComponent.pins) 
                      ? `${selectedLibraryComponent.pins.length} pin(s)` 
                      : 'Pin count unavailable'} */}
                    {Array.isArray(selectedLibraryComponent.pinDetails) 
                      ? `${selectedLibraryComponent.pinDetails.length} pin(s)` 
                      : 'Pin count unavailable'}
                  </small>
                </div>

                {/* Description */}
                <div className="property-group">
                  <label>Description</label>
                  <textarea
                    value={editComponentData.description}
                    onChange={(e) => setEditComponentData(prev => ({ ...prev, description: e.target.value }))}
                    readOnly={!isEditingLibraryComponent}
                    className="property-input"
                    rows="3"
                    style={{
                      resize: 'vertical',
                      ...((!isEditingLibraryComponent) && { backgroundColor: '#f9fafb' })
                    }}
                  />
                </div>

                {/* Specifications */}
                <div className="property-group">
                  <label>Specifications (JSON)</label>
                  <textarea
                    value={typeof editComponentData.specifications === 'string'
                      ? editComponentData.specifications
                      : JSON.stringify(editComponentData.specifications, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setEditComponentData(prev => ({ ...prev, specifications: parsed }));
                      } catch {
                        setEditComponentData(prev => ({ ...prev, specifications: e.target.value }));
                      }
                    }}
                    readOnly={!isEditingLibraryComponent}
                    className="property-input"
                    rows="4"
                    style={{
                      resize: 'vertical',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      ...((!isEditingLibraryComponent) && { backgroundColor: '#f9fafb' })
                    }}
                  />
                </div>

                {/* Simulation Data */}
                <div className="property-group">
                  <label>Simulation Configuration</label>
                  <small style={{ color: '#6b7280', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                    ℹ️ These are default settings. Students can override them during simulation.
                  </small>
                  {/* Show/Hide Toggle */}
                  <button 
                    className="custom-button"
                    style={{
                      backgroundColor: '#0f5bdeff',
                      padding: '0.65rem 0.9rem',
                    }}
                    onClick={() => setShowSimulation(!showSimulation)}>
                    {showSimulation ? '▼ Hide' : '▶ Show'} Simulation Settings
                  </button>
                  
                  {showSimulation && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      backgroundColor: '#f9fafb', 
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      {/* Generation Type */}
                      <div className="property-group">
                        <label>Generation Type</label>
                        <select 
                          value={editComponentData.simulation?.generationLogic?.type || 'random_range'}
                          onChange={(e) => setEditComponentData(prev => ({
                            ...prev,
                            simulation: {
                              ...prev.simulation,
                              generationLogic: {
                                ...prev.simulation?.generationLogic,
                                type: e.target.value
                              }
                            }
                          }))}
                          disabled={!isEditingLibraryComponent}
                          className="property-input"
                          style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                        >
                          <option value="random_range">Random Range</option>
                          {/* <option value="custom">Custom Data</option> */}
                          <option value="sine_wave">Sine Wave</option>
                        </select>
                        
                        {/* If random_range selected, show range inputs */}
                        {editComponentData.simulation?.generationLogic?.type === 'random_range' && (
                          <div style={{ marginTop: '1rem' }}>
                            {/* For each output (temperature, humidity, etc.) */}
                            {editComponentData.simulation?.outputs?.map((output, idx) => (
                              <div key={idx} style={{ 
                                marginBottom: '1rem', 
                                padding: '0.75rem', 
                                backgroundColor: '#eff6ff',
                                borderRadius: '0.375rem',
                                border: '1px solid #bfdbfe'
                              }}>
                                <label style={{ fontWeight: '600', color: '#1e40af', display: 'block', marginBottom: '0.5rem' }}>
                                  {output.name} - Random Range
                                </label>
                                
                                <div>
                                  <label>Min / Max</label>
                                  <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}> 
                                    <input 
                                      type="number" 
                                      value={editComponentData.simulation?.generationLogic?.baseValues?.[output.name]?.min || 0}
                                      onChange={(e) => setEditComponentData(prev => ({
                                        ...prev,
                                        simulation: {
                                          ...prev.simulation,
                                          generationLogic: {
                                            ...prev.simulation?.generationLogic,
                                            baseValues: {
                                              ...prev.simulation?.generationLogic?.baseValues,
                                              [output.name]: {
                                                ...prev.simulation?.generationLogic?.baseValues?.[output.name],
                                                min: parseFloat(e.target.value)
                                              }
                                            }
                                          }
                                        }
                                      }))}
                                      readOnly={!isEditingLibraryComponent}
                                      className="sim-property-input"
                                      placeholder="Min"
                                      style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                                    />
                                    
                                    <input 
                                      type="number" 
                                      value={editComponentData.simulation?.generationLogic?.baseValues?.[output.name]?.max || 100}
                                      onChange={(e) => setEditComponentData(prev => ({
                                        ...prev,
                                        simulation: {
                                          ...prev.simulation,
                                          generationLogic: {
                                            ...prev.simulation?.generationLogic,
                                            baseValues: {
                                              ...prev.simulation?.generationLogic?.baseValues,
                                              [output.name]: {
                                                ...prev.simulation?.generationLogic?.baseValues?.[output.name],
                                                max: parseFloat(e.target.value)
                                              }
                                            }
                                          }
                                        }
                                      }))}
                                      readOnly={!isEditingLibraryComponent}
                                      className="sim-property-input"
                                      placeholder="Max"
                                      style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* If sine_wave selected, show sine parameters */}
                        {editComponentData.simulation?.generationLogic?.type === 'sine_wave' && (
                          <div style={{ marginTop: '1rem' }}>
                            {/* For each output (temperature, humidity, etc.) */}
                            {editComponentData.simulation?.outputs?.map((output, idx) => (
                              <div key={idx} style={{ 
                                marginBottom: '1rem', 
                                padding: '0.75rem', 
                                backgroundColor: '#eff6ff',
                                borderRadius: '0.375rem',
                                border: '1px solid #bfdbfe'
                              }}>
                                <label style={{ fontWeight: '600', color: '#1e40af', display: 'block', marginBottom: '0.5rem' }}>
                                  {output.name} - Sine Wave Parameters
                                </label>
                                
                                {/* Offset and Amplitude */}
                                <div>
                                  <label>Offset / Amplitude</label>
                                  <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                                    <input 
                                      type="number" 
                                      value={editComponentData.simulation?.generationLogic?.sineConfig?.[output.name]?.offset || 0}
                                      onChange={(e) => setEditComponentData(prev => ({
                                        ...prev,
                                        simulation: {
                                          ...prev.simulation,
                                          generationLogic: {
                                            ...prev.simulation?.generationLogic,
                                            sineConfig: {
                                              ...prev.simulation?.generationLogic?.sineConfig,
                                              [output.name]: {
                                                ...prev.simulation?.generationLogic?.sineConfig?.[output.name],
                                                offset: parseFloat(e.target.value)
                                              }
                                            }
                                          }
                                        }
                                      }))}
                                      readOnly={!isEditingLibraryComponent}
                                      className="sim-property-input"
                                      placeholder="Offset (Center)"
                                      style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                                    />
                                    
                                    <input 
                                      type="number" 
                                      value={editComponentData.simulation?.generationLogic?.sineConfig?.[output.name]?.amplitude || 0}
                                      onChange={(e) => setEditComponentData(prev => ({
                                        ...prev,
                                        simulation: {
                                          ...prev.simulation,
                                          generationLogic: {
                                            ...prev.simulation?.generationLogic,
                                            sineConfig: {
                                              ...prev.simulation?.generationLogic?.sineConfig,
                                              [output.name]: {
                                                ...prev.simulation?.generationLogic?.sineConfig?.[output.name],
                                                amplitude: parseFloat(e.target.value)
                                              }
                                            }
                                          }
                                        }
                                      }))}
                                      readOnly={!isEditingLibraryComponent}
                                      className="sim-property-input"
                                      placeholder="Amplitude (±)"
                                      style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                                    />
                                  </div>
                                </div>

                                {/* Frequency and Phase */}
                                <div style={{ marginTop: '0.5rem' }}>
                                  <label>Frequency / Phase</label>
                                  <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                                    <input 
                                      type="number" 
                                      step="0.0001"
                                      value={editComponentData.simulation?.generationLogic?.sineConfig?.[output.name]?.frequency || 0.001}
                                      onChange={(e) => setEditComponentData(prev => ({
                                        ...prev,
                                        simulation: {
                                          ...prev.simulation,
                                          generationLogic: {
                                            ...prev.simulation?.generationLogic,
                                            sineConfig: {
                                              ...prev.simulation?.generationLogic?.sineConfig,
                                              [output.name]: {
                                                ...prev.simulation?.generationLogic?.sineConfig?.[output.name],
                                                frequency: parseFloat(e.target.value)
                                              }
                                            }
                                          }
                                        }
                                      }))}
                                      readOnly={!isEditingLibraryComponent}
                                      className="sim-property-input"
                                      placeholder="Frequency (Hz)"
                                      style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                                    />
                                    
                                    <input 
                                      type="number" 
                                      value={editComponentData.simulation?.generationLogic?.sineConfig?.[output.name]?.phase || 0}
                                      onChange={(e) => setEditComponentData(prev => ({
                                        ...prev,
                                        simulation: {
                                          ...prev.simulation,
                                          generationLogic: {
                                            ...prev.simulation?.generationLogic,
                                            sineConfig: {
                                              ...prev.simulation?.generationLogic?.sineConfig,
                                              [output.name]: {
                                                ...prev.simulation?.generationLogic?.sineConfig?.[output.name],
                                                phase: parseFloat(e.target.value)
                                              }
                                            }
                                          }
                                        }
                                      }))}
                                      readOnly={!isEditingLibraryComponent}
                                      className="sim-property-input"
                                      placeholder="Phase (°)"
                                      style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Update Frequency */}
                        <div style={{ 
                          marginTop: '1rem',
                          padding: '0.75rem', 
                          backgroundColor: '#eff6ff',
                          borderRadius: '0.375rem',
                          border: '1px solid #bfdbfe'
                        }}>
                          <label style={{ fontWeight: '600', color: '#1e40af', display: 'block', marginBottom: '0.5rem' }}>
                            Update Frequency
                          </label>
                          <input 
                            type="number"
                            value={editComponentData.simulation?.generationLogic?.updateFrequency || 1000}
                            onChange={(e) => setEditComponentData(prev => ({
                              ...prev,
                              simulation: {
                                ...prev.simulation,
                                generationLogic: {
                                  ...prev.simulation?.generationLogic,
                                  updateFrequency: parseInt(e.target.value)
                                }
                              }
                            }))}
                            readOnly={!isEditingLibraryComponent}
                            className="sim-property-input"
                            placeholder="Update Frequency (ms)"
                            style={!isEditingLibraryComponent ? { backgroundColor: '#f9fafb' } : {}}
                          />
                          <small style={{ color: '#6b7280', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
                            Time between sensor readings in milliseconds
                          </small>
                        </div>

                      </div>
                    </div>
                  )}
                </div>

                {/* Metadata (Read-only) */}
                <div style={{ 
                  marginTop: '1rem',
                  padding: '0.75rem', 
                  backgroundColor: '#eff6ff',
                  borderRadius: '0.375rem',
                  border: '1px solid #bfdbfe'
                }}>
                  <label style={{ fontWeight: '600', color: '#1e40af', display: 'block', marginBottom: '0.5rem' }}>
                    Metadata
                  </label>
                  
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.85rem', color: '#6b7280' }}>Created By: </label>
                    <input
                      type="text"
                      value={selectedLibraryComponent.createdByName || 'Prof-iX'}
                      readOnly
                      className="property-input"
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '0.85rem', color: '#6b7280' }}>Created At: </label>
                    <input
                      type="text"
                      value={selectedLibraryComponent.createdAt 
                        ? new Date(selectedLibraryComponent.createdAt).toLocaleString()
                        : 'N/A'}
                      readOnly
                      className="property-input"
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  {!isEditingLibraryComponent ? (
                    <>
                      <button
                        className="custom-button"
                        onClick={() => setIsEditingLibraryComponent(true)}
                        style={{
                          backgroundColor: '#3b82f6',
                          padding: '0.65rem 0.9rem',
                          cursor: 'pointer',
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="custom-button"
                        onClick={handleDeleteLibraryComponent}
                        style={{
                          backgroundColor: '#ef4444',
                          padding: '0.65rem 0.9rem',
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="custom-button"
                        onClick={handleUpdateLibraryComponent}
                        style={{
                          backgroundColor: '#10b981',
                          padding: '0.65rem 0.9rem',
                          cursor: 'pointer',
                        }}
                      >
                        💾 Save Changes
                      </button>
                      <button
                        className="custom-button"
                        onClick={() => {
                          setIsEditingLibraryComponent(false);
                          setEditComponentData({
                            name: selectedLibraryComponent.name,
                            category: selectedLibraryComponent.category,
                            needsConfiguration: selectedLibraryComponent.needsConfiguration,
                            icon: selectedLibraryComponent.icon,
                            pinDetails: Array.isArray(selectedLibraryComponent.pinDetails) 
                              ? selectedLibraryComponent.pinDetails.join(', ') 
                              : selectedLibraryComponent.pinDetails,
                            description: selectedLibraryComponent.description || '',
                            specifications: selectedLibraryComponent.specifications || {},
                            simulation: selectedLibraryComponent.simulation || null,
                            codeTemplate: selectedLibraryComponent.codeTemplate || null,
                            documentation: selectedLibraryComponent.documentation || null
                          });
                        }}
                        style={{
                          backgroundColor: '#6b7280',
                          padding: '0.65rem 0.9rem',
                          cursor: 'pointer',
                        }}
                      >
                        ❌ Cancel
                      </button>
                    </>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Educational Info Modal */}
      {infoModalVisible && selectedInfoComponent && (
        <div className="modal-overlay" onClick={() => setInfoModalVisible(false)}>
          <div className="modal-content-top" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="modal-header-top">
              <h3>
                {selectedInfoComponent.icon} {selectedInfoComponent.name}
              </h3>
              <button className="modal-close-btn" 
                onClick={() => setInfoModalVisible(false)}
              >
                X
              </button>
            </div>

            {/* Body with Tabs */}
            <div className="modal-body component-info-body">
              
              {/* Overview Section */}
              <div className="info-section">
                <h3>📋 Overview</h3>
                <p>{selectedInfoComponent.description}</p>
              </div>

              {selectedInfoComponent.specifications && (
                <div className="info-section">
                  <h3>⚙️ Specifications</h3>
                  <table className="specs-table">
                    <tbody>
                      {Object.entries(selectedInfoComponent.specifications || {}).map(([key, value]) => {
                        // Handle nested powerRequirements object
                        if (key === 'powerRequirements' && typeof value === 'object') {
                          return (
                            <tr key={key}>
                              <td colSpan="2">
                                <strong>Power Requirements:</strong>
                                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                                  <li>Needs External Power: {value.needsExternalPower ? 'Yes' : 'No'}</li>
                                  {value.externalVoltage && <li>External Voltage: {value.externalVoltage}</li>}
                                  {value.peakCurrent && <li>Peak Current: {value.peakCurrent}</li>}
                                  <li>Can be Powered from Board: {value.canBePoweredFromBoard ? 'Yes' : 'No'}</li>
                                  {value.notes && <li style={{ color: '#f59e0b', marginTop: '8px' }}>⚠️ {value.notes}</li>}
                                </ul>
                              </td>
                            </tr>
                          );
                        }
                        
                        // Handle other nested objects (if any)
                        if (typeof value === 'object' && value !== null) {
                          return (
                            <tr key={key}>
                              <td><strong>{key}:</strong></td>
                              <td>{JSON.stringify(value)}</td>
                            </tr>
                          );
                        }
                        
                        // Handle normal values
                        return (
                          <tr key={key}>
                            <td><strong>{key}:</strong></td>
                            <td>{value}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pin Details Section */}
              {selectedInfoComponent.pinDetails && selectedInfoComponent.pinDetails.length > 0 && (
                <div className="info-section">
                  <h3>🔌 Pin Configuration</h3>
                  <table className="pin-table">
                    <thead>
                      <tr>
                        <th>Pin</th>
                        <th>Type</th>
                        <th>Mode</th>
                        <th>Description</th>
                        <th>Capabilities</th>
                        <th>Restrictions</th>
                        <th>Warnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInfoComponent.pinDetails.map((pin, idx) => (
                        <tr key={idx}>
                          <td><strong>{pin.pinName}</strong></td>
                          <td><span className={`pin-type ${pin.type}`}>{pin.type}</span></td>
                          <td>{pin.mode || '-'}</td>
                          <td>{pin.description}</td>
                          <td>
                            {pin.capabilities && pin.capabilities.length > 0 ? (
                              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {pin.capabilities.map((cap, i) => (
                                  <li key={i}>{cap}</li>
                                ))}
                              </ul>
                            ) : '-'}
                          </td>
                          <td>
                            {pin.restrictions && pin.restrictions.length > 0 ? (
                              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {pin.restrictions.map((rest, i) => (
                                  <li key={i}>{rest}</li>
                                ))}
                              </ul>
                            ) : '-'}
                          </td>
                          <td>
                            {pin.warnings && pin.warnings.length > 0 ? (
                              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {pin.warnings.map((warn, i) => (
                                  <li key={i} style={{ color: '#f59e0b' }}>{warn}</li>
                                ))}
                              </ul>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Simulation Info Section */}
              {selectedInfoComponent.simulation && (
                <div className="info-section">
                  <h3>🔬 Simulation Outputs</h3>
                  <table className="outputs-table">
                    <thead>
                      <tr>
                        <th>Output</th>
                        <th>Unit</th>
                        <th>Range</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInfoComponent.simulation.outputs?.map((output, idx) => (
                        <tr key={idx}>
                          <td><strong>{output.name}</strong></td>
                          <td>{output.unit}</td>
                          <td>{output.range?.min} - {output.range?.max}</td>
                          <td>{output.dataType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Documentation Section */}
              {selectedInfoComponent.documentation && (
                <div className="details-section">
                  <h4>📚 Documentation & Resources</h4>
                  
                  {selectedInfoComponent.documentation.datasheet && (
                    <a 
                      href={selectedInfoComponent.documentation.datasheet} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="doc-link"
                    >
                      📄 View Datasheet
                    </a>
                  )}

                  {selectedInfoComponent.documentation.tutorials && 
                  selectedInfoComponent.documentation.tutorials.length > 0 && (
                    <div className="doc-links-group">
                      <strong>🎓 Tutorials:</strong>
                      {selectedInfoComponent.documentation.tutorials.map((tutorial, idx) => (
                        <a 
                          key={idx}
                          href={tutorial} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="doc-link small"
                        >
                          Tutorial {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}

                  {selectedInfoComponent.documentation.examples && 
                  selectedInfoComponent.documentation.examples.length > 0 && (
                    <div className="doc-links-group">
                      <strong>💻 Code Examples:</strong>
                      {selectedInfoComponent.documentation.examples.map((example, idx) => (
                        <a 
                          key={idx}
                          href={example} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="doc-link small"
                        >
                          Example {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>

          </div>
        </div>
      )}

      {/* Full-Screen Component Library Modal */}
      {fullScreenLibraryVisible && (
        <div className="fullscreen-library-overlay">
          <div className="fullscreen-library-container">
            
            {/* Header */}
            <div className="fullscreen-library-header">
              <h2>📦 Component Library</h2>
              <button
                className="fullscreen-close-btn"
                onClick={handleCloseFullScreenLibrary}
              >
                ✖
              </button>
            </div>

            {/* Main Content Area */}
            <div className="fullscreen-library-content">
              
              {/* Left Side - Component Cards Grid */}
              <div className="fullscreen-components-grid">
                {CourseDomains[domainName]?.canvases
                    .flatMap(canvas => canvas.categories)
                    .map(category => {
                    const categoryComponents = componentsByCategory[category.key];
                  
                  if (!categoryComponents || categoryComponents.length === 0) {
                    return null;
                  }

                  return (
                    <div key={category.key} className="fullscreen-category-section">
                      <h3 className="fullscreen-category-title">
                        {category.label}
                      </h3>
                      
                      <div className="fullscreen-component-cards">
                        {/* {categoryComponents.map(comp => (
                          <div
                            key={comp.componentId || comp._id}
                            className={`fullscreen-component-card ${
                              selectedFullScreenComponent?.componentId === comp.componentId ? 'selected' : ''
                            }`}
                            onClick={() => handleSelectComponentCard(comp)}
                          >
                            <div className="component-card-icon">{comp.icon}</div>
                            <div className="component-card-name">{comp.name}</div>
                            {comp.specifications?.voltage && (
                              <div className="component-card-spec">
                                ⚡ {comp.specifications.voltage}
                              </div>
                            )}
                          </div>
                        ))} */}
                        {categoryComponents.map(comp => {
                          const fsAuthorized = authData.role === 'admin' ||
                            !comp.courses || comp.courses.length === 0 ||
                            (courseInfo?.title && comp.courses.includes(courseInfo.title));

                          const fsLockTooltip = !fsAuthorized
                            ? `🔒 Not available for this course. Designed for: ${comp.courses?.join(', ')}`
                            : comp.name;

                          return (
                            <div
                              key={comp.componentId || comp._id}
                              className={`fullscreen-component-card ${
                                selectedFullScreenComponent?.componentId === comp.componentId ? 'selected' : ''
                              } ${!fsAuthorized ? 'fullscreen-component-card--locked' : ''}`}
                              onClick={() => fsAuthorized && handleSelectComponentCard(comp)}
                              title={fsLockTooltip}
                              style={{ cursor: fsAuthorized ? 'pointer' : 'not-allowed' }}
                            >
                              {/* Lock badge */}
                              {!fsAuthorized && (
                                <div className="fullscreen-lock-badge" title={fsLockTooltip}>
                                  🔒
                                </div>
                              )}
                              <div className="component-card-icon">{comp.icon}</div>
                              <div className="component-card-name">{comp.name}</div>
                              {comp.specifications?.voltage && (
                                <div className="component-card-spec">
                                  ⚡ {comp.specifications.voltage}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Side - Component Details */}
              <div className="fullscreen-component-details">
                {selectedFullScreenComponent ? (
                  <>
                    {/* Component Header */}
                    <div className="details-header">
                      <span className="details-icon">{selectedFullScreenComponent.icon}</span>
                      <div>
                        <h2>{selectedFullScreenComponent.name}</h2>
                        <span className="details-category">
                            {CourseDomains[domainName]?.canvases
                                .flatMap(canvas => canvas.categories)
                                .find(c => c.key === selectedFullScreenComponent.category)?.label}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    {selectedFullScreenComponent.description && (
                      <div className="details-section">
                        <h4>📝 Description</h4>
                        <p>{selectedFullScreenComponent.description}</p>
                      </div>
                    )}

                    {/* Specifications */}
                    {selectedFullScreenComponent.specifications && 
                    Object.keys(selectedFullScreenComponent.specifications).length > 0 && (
                      <div className="details-section">
                        <h4>⚙️ Specifications</h4>
                        <div className="specs-grid">
                          {Object.entries(selectedFullScreenComponent.specifications).map(([key, value]) => (
                            <div key={key} className="spec-item">
                              <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong>
                              <span>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pins */}
                    {/* {selectedFullScreenComponent.pins && selectedFullScreenComponent.pins.length > 0 && ( */}
                    {selectedFullScreenComponent.pinDetails && selectedFullScreenComponent.pinDetails.length > 0 && (
                      <div className="details-section">
                        {/* <h4>📌 Pins ({selectedFullScreenComponent.pins.length})</h4> */}
                        <h4>📌 Pin Details ({selectedFullScreenComponent.pinDetails.length})</h4>
                        <div className="pins-list">
                          {/* {selectedFullScreenComponent.pinDetails.map((pin, idx) => (
                            <span key={idx} className="pin-badge">{pin}</span> */}
                          {selectedFullScreenComponent.pinDetails.map((pinDetail, idx) => (
                            <span key={idx} className="pin-badge">{pinDetail.pinName}: {pinDetail.description}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Courses */}
                    {selectedFullScreenComponent.courses && selectedFullScreenComponent.courses.length > 0 && (
                      <div className="info-section">
                        <h3>🎓 Available in Courses</h3>
                        <div className="courses-list">
                          {selectedFullScreenComponent.courses.map((course, index) => (
                            <span key={index} className="course-badge">
                              {course.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Documentation */}
                    {selectedFullScreenComponent.documentation && (
                      <div className="details-section">
                        <h4>📚 Documentation & Resources</h4>
                        
                        {selectedFullScreenComponent.documentation.datasheet && (
                          <a 
                            href={selectedFullScreenComponent.documentation.datasheet} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="doc-link"
                          >
                            📄 View Datasheet
                          </a>
                        )}

                        {selectedFullScreenComponent.documentation.tutorials && 
                        selectedFullScreenComponent.documentation.tutorials.length > 0 && (
                          <div className="doc-links-group">
                            <strong>🎓 Tutorials:</strong>
                            {selectedFullScreenComponent.documentation.tutorials.map((tutorial, idx) => (
                              <a 
                                key={idx}
                                href={tutorial} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="doc-link small"
                              >
                                Tutorial {idx + 1}
                              </a>
                            ))}
                          </div>
                        )}

                        {selectedFullScreenComponent.documentation.examples && 
                        selectedFullScreenComponent.documentation.examples.length > 0 && (
                          <div className="doc-links-group">
                            <strong>💻 Code Examples:</strong>
                            {selectedFullScreenComponent.documentation.examples.map((example, idx) => (
                              <a 
                                key={idx}
                                href={example} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="doc-link small"
                              >
                                Example {idx + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  </>
                ) : (
                  <div className="details-placeholder">
                    <div className="placeholder-icon">👈</div>
                    <h3>Select a Component</h3>
                    <p>Click on any component card to view its details and specifications</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── Node-RED Flow Editor Modal ─────────────────────────────────────── */}
      {flowEditorVisible && selectedGatewayComponent && (
        <div className="modal-overlay" onClick={() => setFlowEditorVisible(false)}>
          <div
            style={{backgroundColor: 'azure'}}
            // style={{ maxWidth: '480px' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header-top">
              <h2>🔴 {selectedGatewayComponent.label || selectedGatewayComponent.componentName}</h2>
              <button className="modal-close-btn" onClick={() => setFlowEditorVisible(false)}>✖</button>
            </div>

            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>

              {/* Flow stats */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#8F0000' }}>
                    {selectedGatewayComponent.nodeRedFlow?.nodes?.length || 0}
                  </div>
                  <div style={{ fontSize: '12px' }}>Nodes</div>
                </div>
                <div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#8F0000' }}>
                    {selectedGatewayComponent.nodeRedFlow?.wires?.length || 0}
                  </div>
                  <div style={{ fontSize: '12px' }}>Wires</div>
                </div>
                <div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#8F0000' }}>
                    {selectedGatewayComponent.flowSessionId ? '✓' : '—'}
                  </div>
                  <div style={{ fontSize: '12px' }}>Session</div>
                </div>
              </div>

              {selectedGatewayComponent.flowSessionId && (
                <div style={{ fontSize: '11px', color: '#475569', marginBottom: '20px', fontFamily: 'monospace' }}>
                  {selectedGatewayComponent.flowSessionId}
                </div>
              )}

              <p style={{ fontSize: '13px', marginBottom: '24px' }}>
                Open the flow canvas in a new tab to drag and drop Node-RED nodes
                and build your IIoT data pipeline.
              </p>

              <button
                style={{
                  padding: '10px 28px',
                  background: '#8F0000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  width: '100%'
                }}
                onClick={() => {
                  setFlowEditorVisible(false);
                  const url = `/labengine/course/${courseId}/domain/NodeRed/buildcanvas` +
                    `?sessionId=${selectedGatewayComponent.flowSessionId}` +
                    `&gatewayInstanceId=${selectedGatewayComponent.instanceId}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                  // window.open(url, '_blank', 'noreferrer');
                }}
              >
                🔴 Open Flow Canvas
              </button>

            </div>
          </div>
        </div>
      )}

      <ProfIXChatbot 
        userName={authData.name}
        courseId={courseId} 
        courseName={courseInfo?.title} 
        category={courseInfo?.category}
      />

      {/* Delete Confirmation Alert */}
      <CustomAlert
        visible={alertVisible}
        title={alertConfig.title}
        icon={alertConfig.icon}
        message={alertConfig.message}
        onConfirm={alertConfig.onConfirm}
        onCancel={() => setAlertVisible(false)}
      />

      {/* Error Display */}
      {error && (
        <div className="circuit-error-container">
          ❌ Error: {error}
          <button onClick={clearError} className="circuit-error-close">
            ✖
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loader" />
          <p>Loading circuit...</p>
        </div>
      )}

    </div>
  );
};

export default CanvasBuilder;